import {
    Badge,
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    Checkbox,
    Collapsible,
    Divider,
    IndexTable,
    Icon,
    InlineStack,
    Layout,
    Link,
    Modal,
    Page,
    Select,
    SkeletonBodyText,
    SkeletonPage,
    Text,
    TextField,
} from "@shopify/polaris";
import {
    ConnectIcon,
    DeleteIcon,
    DatabaseIcon,
    EditIcon,
    FormsIcon,
    LanguageTranslateIcon,
    MenuIcon,
    MetafieldsIcon,
    PaintBrushRoundIcon,
    PlanIcon,
    PlusIcon,
    RefreshIcon,
    SearchIcon,
    SettingsIcon,
} from "@shopify/polaris-icons";
import {
    SafeAppBridgeModal as AppBridgeModal,
    SafeAppBridgeTitleBar as TitleBar,
    SafeSaveBar as SaveBar,
} from "../../app/components/SafeAppBridge";
import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import agent from "../../app/api/agent";
import type { StorefrontThemeSettings } from "../../app/models/StorefrontThemeSettings";
import type { UserSetting } from "../../app/models/UserSetting";
import type {
    StorefrontCollectionOption,
    StorefrontButtonBehavior,
    StorefrontCuratedGroup,
    StorefrontResultsSortMode,
    StorefrontResultFilterMode,
    StorefrontRenderingMode,
    StorefrontReviewDisplayMode,
    StorefrontResultsGroupingMode,
    StorefrontResultsSettings,
    StorefrontResultsViewMode,
    StorefrontUiSettings,
    StorefrontVariantResultDisplayMode,
} from "../../app/models/StorefrontResultsSettings";
import { useStore } from "../../app/stores/store";
import { getLastAppRoute } from "../../app/utils/lastAppRoute";
import DataSyncSettings from "../settings/DataSyncSettings";
import FitmentFieldsSettings, { type FitmentFieldsEditorStatus } from "../settings/FitmentFieldsSettings";
import MetafieldAttributesSettings, { type MetafieldAttributesEditorStatus } from "../settings/MetafieldAttributesSettings";
import { LoadingSettingsCard, SettingCheckbox, SettingsCard } from "../settings/SettingsControls";
import StorefrontTranslationsSettings, { type StorefrontTranslationsEditorStatus } from "../settings/StorefrontTranslationsSettings";
import Turn14IntegrationSettings, { type IntegrationEditorStatus } from "../settings/Turn14IntegrationSettings";
import AtdIntegrationSettings from "../settings/AtdIntegrationSettings";
import PlanPage from "../plan/PlanPage";

const DEFAULT_THEME_SETTINGS: StorefrontThemeSettings = {
    isThemeSet: true,
    promptMake: "---Select Make---",
    promptModel: "---Select Model---",
    promptYear: "---Select Year---",
    promptSubmodel: "---Select Submodel---",
    promptSearch: "Search",
    promptClear: "Clear",
    redirectPath: "/collections/all/{vehicle}",
    collectionTagFilterPattern: "?filter.p.tag={vehicle}",
    automaticSearch: false,
    ymmEnabled: false,
    sortYearsASC: false,
    redirectToResults: false,
    customRedirectEnabled: false,
    globalFilter: false,
    collectionRedirectEnabled: false,
    collectionTagFilterEnabled: false,
    clearButtonRedirectEnabled: false,
    includeUniversalCollections: false,
    useTwoTags: false,
    useThreeTags: false,
    isPartialSearch: false,
    isCollectionMetaField: false,
    metaDataSyncEnabled: false,
    submodelEnabled: false,
    tagPrefixEnabled: true,
    saveTagToCustomerProfile: false,
    onlyShowMappedUserVehicles: true,
    searchableDropdownsEnabled: false,
    renderingMode: "CollectionFiltering",
    resultsReturnType: "Products",
    resultsGroupingMode: "None",
    priceDisplayMode: "MarketAwarePrices",
    variantResultDisplayMode: "TopLevelProducts",
};

const DEFAULT_UI_SETTINGS: StorefrontUiSettings = {
    globalPrimaryColor: "#104190",
    globalSecondaryColor: "#000000",
    globalAccentColor: "#087f3f",
    globalSurfaceColor: "#ffffff",
    globalSubtleBackgroundColor: "#E1E3E4",
    globalTextColor: "#111111",
    globalInverseTextColor: "#ffffff",
    globalMutedTextColor: "#4b5563",
    globalBorderColor: "#D1D5DB",
    globalReviewStarColor: "#F5A623",
    colorOverrides: [],
    searchContentWidth: 1200,
    searchBorderRadius: 8,
    searchPadding: 10,
    searchBarBackground: "#E1E3E4",
    searchButtonBackground: "#104190",
    searchButtonText: "#ffffff",
    clearButtonBackground: "#000000",
    clearButtonText: "#ffffff",
    selectedVehicleBackground: "#104190",
    selectedVehicleText: "#ffffff",
    changeButtonBackground: "#ffffff",
    changeButtonText: "#111111",
    disableResultStyles: false,
    resultsSortBy: "title-asc",
    showFitmentBadges: true,
    showStockStatus: true,
    showViewAsLabel: false,
    showVendor: false,
    showSku: false,
    showFitmentNotes: false,
    reviewDisplayMode: "none",
    showReviewCount: true,
    hoverAnimation: true,
    dynamicCardSizing: false,
    showGroupSelector: true,
    showScrollTop: true,
    stickyGroupHeadings: false,
    stickyHeadingOffset: 0,
    enableViewToggle: true,
    defaultView: "grid",
    resultsContentWidth: 1200,
    productsPerRowDesktop: 4,
    productsPerRowTablet: 3,
    productsPerRowMobile: 2,
    cardImageHeight: 220,
    titleMaxLines: 0,
    minCardWidth: 220,
    headingBackground: "#111111",
    headingText: "#ffffff",
    cardBackground: "#ffffff",
    cardText: "#111111",
    buttonBackground: "#104190",
    buttonText: "#ffffff",
    exactBadgeColor: "#087f3f",
    universalBadgeColor: "#4b5563",
    hoverBorderColor: "#104190",
    reviewStarColor: "#F5A623",
    reviewEmptyStarColor: "#D1D5DB",
    buttonBehavior: "view-product",
};

const DEFAULT_RESULTS_SETTINGS: StorefrontResultsSettings = {
    renderingMode: "CollectionFiltering",
    returnResultsAs: "Products",
    groupResultsBy: "None",
    priceDisplayMode: "MarketAwarePrices",
    variantResultDisplayMode: "TopLevelProducts",
    productTypeFilterMode: "All",
    productTypeFilterValues: [],
    collectionFilterMode: "All",
    collectionFilterIds: [],
    uiSettings: DEFAULT_UI_SETTINGS,
    curatedGroups: [],
    availableProductTypes: [],
    availableCollections: [],
};

const APP_PROXY_RESULTS_PATH = "/a/partfits/results";

const modeOptions = [
    {
        label: "Dedicated results page",
        value: "RenderedResults",
        description: "Display matching products on a Shopify page using the PartFits Results block.",
    },
    {
        label: "App proxy results page",
        value: "AppProxyResults",
        description: `Use the hosted PartFits page at ${APP_PROXY_RESULTS_PATH}. No Shopify page or app block is required.`,
    },
    {
        label: "Shopify collection filtering",
        value: "CollectionFiltering",
        description: "Filter a Shopify collection and let the active theme display matching products.",
    },
] satisfies Array<{
    label: string;
    value: StorefrontRenderingMode;
    description: string;
}>;

type SearchResultModeCardProps = {
    checked: boolean;
    description: string;
    id: string;
    label: string;
    onChange: () => void;
};

const SearchResultModeCard = ({ checked, description, id, label, onChange }: SearchResultModeCardProps) => (
    <label
        className={`pf-settings-choice-card${checked ? " pf-settings-choice-card--selected" : ""}`}
        htmlFor={id}
    >
        <input
            className="pf-settings-choice-card__radio"
            id={id}
            type="radio"
            name="search-result-mode"
            checked={checked}
            onChange={onChange}
        />
        <span className="pf-settings-choice-card__content">
            <Text as="span" variant="bodyMd" fontWeight="semibold">{label}</Text>
            <Text as="span" variant="bodySm" tone="subdued">{description}</Text>
        </span>
    </label>
);

type SettingsRadioOption = {
    description: string;
    label: string;
    value: string;
};

type SettingsRadioGroupProps = {
    hideLabel?: boolean;
    label: string;
    name: string;
    onChange: (value: string) => void;
    options: readonly SettingsRadioOption[];
    value: string;
};

const SettingsRadioGroup = ({ hideLabel = false, label, name, onChange, options, value }: SettingsRadioGroupProps) => (
    <div className="pf-settings-radio-field">
        {!hideLabel ? (
            <Text as="h3" variant="headingSm">{label}</Text>
        ) : null}
        <div className="pf-settings-radio-group" role="radiogroup" aria-label={label}>
            {options.map((option) => {
                const checked = value === option.value;
                const id = `${name}-${option.value}`;

                return (
                    <label
                        key={option.value}
                        className={`pf-settings-radio-row${checked ? " pf-settings-radio-row--selected" : ""}`}
                        htmlFor={id}
                    >
                        <input
                            className="pf-settings-radio-row__radio"
                            id={id}
                            type="radio"
                            name={name}
                            checked={checked}
                            onChange={() => onChange(option.value)}
                        />
                        <span className="pf-settings-radio-row__content">
                            <Text as="span" variant="bodyMd" fontWeight="medium">{option.label}</Text>
                            <Text as="span" variant="bodySm" tone="subdued">{option.description}</Text>
                        </span>
                    </label>
                );
            })}
        </div>
    </div>
);

const groupingOptions = [
    { label: "Do not group products", value: "None" },
    { label: "Product type", value: "ProductType" },
    { label: "Collection", value: "Collection" },
    { label: "Custom groups", value: "CuratedGroups" },
];

const variantResultDisplayOptions = [
    {
        label: "One product card per product",
        value: "TopLevelProducts",
        description: "Combine matching variants into one product card and open the matched variant when there is only one.",
    },
    {
        label: "Separate card for each matching variant",
        value: "SeparateVariants",
        description: "Show every matching variant as its own result card.",
    },
];

type SearchDestinationMode = "CurrentCollection" | "ResultsPage";
type ResultsPageLocationMode = "Default" | "Custom";
type ShopifyFilteringMethod = "StorefrontFiltering" | "LegacyTagUrl";

const shopifyFilteringMethodOptions = [
    {
        label: "Shopify storefront filtering (Search & Discovery)",
        value: "StorefrontFiltering",
        description: "Use Shopify's native tag filters with a compatible theme and catalog.",
    },
    {
        label: "Legacy collection tag URL",
        value: "LegacyTagUrl",
        description: "Add the selected vehicle tag to the collection URL for older or custom themes.",
    },
];

const searchDestinationOptions = [
    {
        label: "Use current collection when available",
        value: "CurrentCollection",
        description: "Keep shoppers in the current collection when possible; searches elsewhere use the results page.",
    },
    {
        label: "Always use results page",
        value: "ResultsPage",
        description: "Send every search to the configured results page.",
    },
];

const resultsPageLocationOptions = [
    {
        label: "Default all-products results page",
        value: "Default",
        description: "Use /collections/all whenever a separate results page is needed.",
    },
    {
        label: "Custom path",
        value: "Custom",
        description: "Send searches to a Shopify page or collection path you specify.",
    },
];

const searchTriggerOptions = [
    {
        label: "When customer clicks Search",
        value: "Manual",
        description: "Let customers review their vehicle selections before starting the search.",
    },
    {
        label: "Automatically after required fields are selected",
        value: "Automatic",
        description: "Start the search as soon as every required vehicle field has a value.",
    },
];

const resultFilterModeOptions = [
    { label: "Show all", value: "All" },
    { label: "Only selected", value: "Include" },
    { label: "Hide selected", value: "Exclude" },
];

const resultsSortOptions = [
    { label: "Title A-Z", value: "title-asc" },
    { label: "Title Z-A", value: "title-desc" },
    { label: "Price low to high", value: "price-asc" },
    { label: "Price high to low", value: "price-desc" },
    { label: "Exact fit first", value: "exact-fit-first" },
];

const resultsViewOptions = [
    { label: "Grid", value: "grid" },
    { label: "List", value: "list" },
];

const titleMaxLinesOptions = [
    { label: "Unlimited", value: "0" },
    { label: "1 line", value: "1" },
    { label: "2 lines", value: "2" },
    { label: "3 lines", value: "3" },
    { label: "4 lines", value: "4" },
];

const reviewDisplayOptions = [
    { label: "Off", value: "none" },
    { label: "Shopify review metafields", value: "shopify-metafields" },
];

const buttonBehaviorOptions = [
    { label: "View product", value: "view-product" },
    { label: "Add to cart", value: "add-to-cart" },
];

type AppearancePanelKey = "global" | "vehicle-search" | "results-layout" | "product-cards" | "result-colors";
type UiColorOverrideKey =
    | "searchBarBackground"
    | "searchButtonBackground"
    | "searchButtonText"
    | "clearButtonBackground"
    | "clearButtonText"
    | "selectedVehicleBackground"
    | "selectedVehicleText"
    | "changeButtonBackground"
    | "changeButtonText"
    | "headingBackground"
    | "headingText"
    | "cardBackground"
    | "cardText"
    | "buttonBackground"
    | "buttonText"
    | "exactBadgeColor"
    | "universalBadgeColor"
    | "hoverBorderColor"
    | "reviewStarColor"
    | "reviewEmptyStarColor";
type UiGlobalColorKey =
    | "globalPrimaryColor"
    | "globalSecondaryColor"
    | "globalAccentColor"
    | "globalSurfaceColor"
    | "globalSubtleBackgroundColor"
    | "globalTextColor"
    | "globalInverseTextColor"
    | "globalMutedTextColor"
    | "globalBorderColor"
    | "globalReviewStarColor";

const appearancePanelOptions = [
    { label: "Global style", value: "global" },
    { label: "Vehicle search bar", value: "vehicle-search" },
    { label: "Results layout", value: "results-layout" },
    { label: "Product cards", value: "product-cards" },
    { label: "Result colors", value: "result-colors" },
];

const uiColorOverrideKeys: UiColorOverrideKey[] = [
    "searchBarBackground",
    "searchButtonBackground",
    "searchButtonText",
    "clearButtonBackground",
    "clearButtonText",
    "selectedVehicleBackground",
    "selectedVehicleText",
    "changeButtonBackground",
    "changeButtonText",
    "headingBackground",
    "headingText",
    "cardBackground",
    "cardText",
    "buttonBackground",
    "buttonText",
    "exactBadgeColor",
    "universalBadgeColor",
    "hoverBorderColor",
    "reviewStarColor",
    "reviewEmptyStarColor",
];

const getSearchDestinationMode = (theme?: StorefrontThemeSettings): SearchDestinationMode =>
    theme?.redirectToResults ? "ResultsPage" : "CurrentCollection";

const getResultsPageLocationMode = (theme?: StorefrontThemeSettings): ResultsPageLocationMode =>
    theme?.customRedirectEnabled ? "Custom" : "Default";

const getShopifyFilteringMethod = (theme?: StorefrontThemeSettings): ShopifyFilteringMethod =>
    theme?.collectionTagFilterEnabled ? "StorefrontFiltering" : "LegacyTagUrl";

const stripVehiclePlaceholderFromPath = (path?: string): string => {
    const normalized = (path || "/collections/all").trim() || "/collections/all";
    return normalized
        .replace(/\/+\{vehicle\}/gi, "")
        .replace(/\{vehicle\}/gi, "")
        .replace(/\/+$/g, "") || "/collections/all";
};

const resultSettingsSnapshot = (settings?: StorefrontResultsSettings) => JSON.stringify({
    renderingMode: settings?.renderingMode ?? DEFAULT_RESULTS_SETTINGS.renderingMode,
    returnResultsAs: settings?.returnResultsAs ?? DEFAULT_RESULTS_SETTINGS.returnResultsAs,
    groupResultsBy: settings?.groupResultsBy ?? DEFAULT_RESULTS_SETTINGS.groupResultsBy,
    priceDisplayMode: settings?.priceDisplayMode ?? DEFAULT_RESULTS_SETTINGS.priceDisplayMode,
    variantResultDisplayMode: settings?.variantResultDisplayMode ?? DEFAULT_RESULTS_SETTINGS.variantResultDisplayMode,
    productTypeFilterMode: settings?.productTypeFilterMode ?? DEFAULT_RESULTS_SETTINGS.productTypeFilterMode,
    productTypeFilterValues: [...(settings?.productTypeFilterValues ?? [])].sort((left, right) => left.localeCompare(right)),
    collectionFilterMode: settings?.collectionFilterMode ?? DEFAULT_RESULTS_SETTINGS.collectionFilterMode,
    collectionFilterIds: [...(settings?.collectionFilterIds ?? [])].sort((left, right) => left - right),
    uiSettings: uiSettingsSnapshot(settings?.uiSettings),
    curatedGroups: [...(settings?.curatedGroups ?? [])]
        .map((group) => ({
            id: group.id ?? null,
            name: group.name.trim(),
            include: group.include,
            displayOrder: Number(group.displayOrder) || 0,
            collectionIds: [...(group.collectionIds ?? [])].sort((left, right) => left - right),
        }))
        .sort((left, right) => left.displayOrder - right.displayOrder || left.name.localeCompare(right.name)),
});

const themeSettingsSnapshot = (
    theme: StorefrontThemeSettings | undefined,
    persistentFilteringChecked: boolean,
    submodelEnabled: boolean
) => JSON.stringify({
    redirectPath: theme?.redirectPath ?? DEFAULT_THEME_SETTINGS.redirectPath,
    collectionTagFilterPattern: theme?.collectionTagFilterPattern ?? DEFAULT_THEME_SETTINGS.collectionTagFilterPattern,
    automaticSearch: !!theme?.automaticSearch,
    ymmEnabled: !!theme?.ymmEnabled,
    sortYearsASC: !!theme?.sortYearsASC,
    redirectToResults: !!theme?.redirectToResults,
    customRedirectEnabled: !!theme?.customRedirectEnabled,
    collectionRedirectEnabled: !!theme?.collectionRedirectEnabled,
    collectionTagFilterEnabled: !!theme?.collectionTagFilterEnabled,
    clearButtonRedirectEnabled: !!theme?.clearButtonRedirectEnabled,
    includeUniversalCollections: !!theme?.includeUniversalCollections,
    useTwoTags: !!theme?.useTwoTags,
    useThreeTags: !!theme?.useThreeTags,
    isPartialSearch: !!theme?.isPartialSearch,
    isCollectionMetaField: !!theme?.isCollectionMetaField,
    metaDataSyncEnabled: !!theme?.metaDataSyncEnabled,
    saveTagToCustomerProfile: !!theme?.saveTagToCustomerProfile,
    onlyShowMappedUserVehicles: theme?.onlyShowMappedUserVehicles ?? true,
    searchableDropdownsEnabled: !!theme?.searchableDropdownsEnabled,
    persistentFilteringChecked,
    submodelEnabled,
});

const cloneResultsSettings = (settings: StorefrontResultsSettings): StorefrontResultsSettings => ({
    ...settings,
    productTypeFilterValues: [...settings.productTypeFilterValues],
    collectionFilterIds: [...settings.collectionFilterIds],
    uiSettings: withUiDefaults(settings.uiSettings),
    curatedGroups: settings.curatedGroups.map((group) => ({
        ...group,
        collectionIds: [...group.collectionIds],
    })),
    availableProductTypes: [...settings.availableProductTypes],
    availableCollections: [...settings.availableCollections],
});

const createBlankGroup = (displayOrder: number): StorefrontCuratedGroup => ({
    name: "",
    include: true,
    displayOrder,
    collectionIds: [],
});

const collectionLabel = (collection: StorefrontCollectionOption) =>
    collection.handle ? `${collection.title} (${collection.handle})` : collection.title;

const normalizeColorPickerValue = (value?: string) =>
    /^#[0-9a-fA-F]{6}$/.test(value || "") ? value! : "#000000";

const normalizeHexColor = (value: string | undefined, fallback: string) =>
    /^#[0-9a-fA-F]{6}$/.test(value || "") ? value! : fallback;

const getInheritedUiColors = (settings: StorefrontUiSettings): Record<UiColorOverrideKey, string> => ({
    searchBarBackground: settings.globalSubtleBackgroundColor,
    searchButtonBackground: settings.globalPrimaryColor,
    searchButtonText: settings.globalInverseTextColor,
    clearButtonBackground: settings.globalSecondaryColor,
    clearButtonText: settings.globalInverseTextColor,
    selectedVehicleBackground: settings.globalPrimaryColor,
    selectedVehicleText: settings.globalInverseTextColor,
    changeButtonBackground: settings.globalSurfaceColor,
    changeButtonText: settings.globalTextColor,
    headingBackground: settings.globalTextColor,
    headingText: settings.globalInverseTextColor,
    cardBackground: settings.globalSurfaceColor,
    cardText: settings.globalTextColor,
    buttonBackground: settings.globalPrimaryColor,
    buttonText: settings.globalInverseTextColor,
    exactBadgeColor: settings.globalAccentColor,
    universalBadgeColor: settings.globalMutedTextColor,
    hoverBorderColor: settings.globalPrimaryColor,
    reviewStarColor: settings.globalReviewStarColor,
    reviewEmptyStarColor: settings.globalBorderColor,
});

const normalizeUiColorOverrides = (overrides?: string[]) =>
    uiColorOverrideKeys.filter((key) => overrides?.includes(key));

const inferLegacyUiColorOverrides = (settings: StorefrontUiSettings) => {
    const inherited = getInheritedUiColors(settings);
    return uiColorOverrideKeys.filter((key) => normalizeHexColor(settings[key], inherited[key]).toLowerCase() !== inherited[key].toLowerCase());
};

const withUiDefaults = (settings?: Partial<StorefrontUiSettings>): StorefrontUiSettings => {
    const hasExplicitOverrides = Array.isArray(settings?.colorOverrides);
    const merged = {
        ...DEFAULT_UI_SETTINGS,
        ...(settings ?? {}),
        colorOverrides: hasExplicitOverrides ? [...(settings?.colorOverrides ?? [])] : [],
    } as StorefrontUiSettings;

    const normalizedGlobals = {
        ...merged,
        globalPrimaryColor: normalizeHexColor(merged.globalPrimaryColor, DEFAULT_UI_SETTINGS.globalPrimaryColor),
        globalSecondaryColor: normalizeHexColor(merged.globalSecondaryColor, DEFAULT_UI_SETTINGS.globalSecondaryColor),
        globalAccentColor: normalizeHexColor(merged.globalAccentColor, DEFAULT_UI_SETTINGS.globalAccentColor),
        globalSurfaceColor: normalizeHexColor(merged.globalSurfaceColor, DEFAULT_UI_SETTINGS.globalSurfaceColor),
        globalSubtleBackgroundColor: normalizeHexColor(merged.globalSubtleBackgroundColor, DEFAULT_UI_SETTINGS.globalSubtleBackgroundColor),
        globalTextColor: normalizeHexColor(merged.globalTextColor, DEFAULT_UI_SETTINGS.globalTextColor),
        globalInverseTextColor: normalizeHexColor(merged.globalInverseTextColor, DEFAULT_UI_SETTINGS.globalInverseTextColor),
        globalMutedTextColor: normalizeHexColor(merged.globalMutedTextColor, DEFAULT_UI_SETTINGS.globalMutedTextColor),
        globalBorderColor: normalizeHexColor(merged.globalBorderColor, DEFAULT_UI_SETTINGS.globalBorderColor),
        globalReviewStarColor: normalizeHexColor(merged.globalReviewStarColor, DEFAULT_UI_SETTINGS.globalReviewStarColor),
    };

    return {
        ...normalizedGlobals,
        colorOverrides: hasExplicitOverrides
            ? normalizeUiColorOverrides(normalizedGlobals.colorOverrides)
            : inferLegacyUiColorOverrides(normalizedGlobals),
    };
};

// Effective colors are inherited from Global style until a specific control is edited.
const getEffectiveUiSettings = (settings: StorefrontUiSettings): StorefrontUiSettings => {
    const base = withUiDefaults(settings);
    const inherited = getInheritedUiColors(base);
    const overrides = new Set(base.colorOverrides);
    const effective = { ...base };

    uiColorOverrideKeys.forEach((key) => {
        effective[key] = overrides.has(key)
            ? normalizeHexColor(base[key], inherited[key])
            : inherited[key];
    });

    return effective;
};

const uiSettingsSnapshot = (settings?: StorefrontUiSettings) =>
    JSON.stringify(withUiDefaults(settings));

const withResultsDefaults = (settings?: Partial<StorefrontResultsSettings>): StorefrontResultsSettings => ({
    ...DEFAULT_RESULTS_SETTINGS,
    ...(settings ?? {}),
    productTypeFilterValues: [...(settings?.productTypeFilterValues ?? DEFAULT_RESULTS_SETTINGS.productTypeFilterValues)],
    collectionFilterIds: [...(settings?.collectionFilterIds ?? DEFAULT_RESULTS_SETTINGS.collectionFilterIds)],
    uiSettings: withUiDefaults(settings?.uiSettings),
    curatedGroups: [...(settings?.curatedGroups ?? DEFAULT_RESULTS_SETTINGS.curatedGroups)],
    availableProductTypes: [...(settings?.availableProductTypes ?? DEFAULT_RESULTS_SETTINGS.availableProductTypes)],
    availableCollections: [...(settings?.availableCollections ?? DEFAULT_RESULTS_SETTINGS.availableCollections)],
});

interface ColorSettingProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    helpText?: string;
}

function ColorSetting({ label, value, onChange, helpText }: ColorSettingProps) {
    return (
        <BlockStack gap="100">
            <Text as="span" variant="bodyMd">{label}</Text>
            <InlineStack gap="200" blockAlign="center" wrap={false}>
                <input
                    aria-label={`${label} color picker`}
                    type="color"
                    value={normalizeColorPickerValue(value)}
                    onChange={(event) => onChange(event.currentTarget.value)}
                    style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 8,
                        cursor: "pointer",
                        height: 36,
                        padding: 2,
                        width: 44,
                    }}
                />
                <div style={{ flex: "1 1 160px" }}>
                    <TextField
                        label={label}
                        labelHidden
                        autoComplete="off"
                        value={value}
                        onChange={onChange}
                        placeholder="#104190"
                    />
                </div>
            </InlineStack>
            {helpText ? (
                <Text as="p" variant="bodySm" tone="subdued">{helpText}</Text>
            ) : null}
        </BlockStack>
    );
}

interface AppearanceControlsProps {
    uiSettings: StorefrontUiSettings;
    updateUiSettingsDraft: (changes: Partial<StorefrontUiSettings>) => void;
    updateUiNumberSetting: (key: keyof StorefrontUiSettings, value: string) => void;
}

interface InheritedColorSettingProps {
    label: string;
    colorKey: UiColorOverrideKey;
    uiSettings: StorefrontUiSettings;
    onChange: (key: UiColorOverrideKey, value: string) => void;
    onReset: (key: UiColorOverrideKey) => void;
}

function InheritedColorSetting({ label, colorKey, uiSettings, onChange, onReset }: InheritedColorSettingProps) {
    const inheritedValue = getInheritedUiColors(uiSettings)[colorKey];
    const isOverridden = uiSettings.colorOverrides.includes(colorKey);

    return (
        <BlockStack gap="150">
            <ColorSetting
                label={label}
                value={uiSettings[colorKey]}
                onChange={(value) => onChange(colorKey, value)}
                helpText={isOverridden ? "Custom override. Reset it to follow Global style again." : `Inherited from Global style (${inheritedValue}).`}
            />
            {isOverridden ? (
                <InlineStack align="end">
                    <Button variant="tertiary" onClick={() => onReset(colorKey)}>Reset to global</Button>
                </InlineStack>
            ) : null}
        </BlockStack>
    );
}

function AppearanceControls({ uiSettings, updateUiSettingsDraft, updateUiNumberSetting }: AppearanceControlsProps) {
    const [activePanel, setActivePanel] = useState<AppearancePanelKey>("global");

    const updateGlobalColor = (key: UiGlobalColorKey, value: string) => {
        updateUiSettingsDraft({ [key]: value } as Partial<StorefrontUiSettings>);
    };

    const updateOverrideColor = (key: UiColorOverrideKey, value: string) => {
        updateUiSettingsDraft({
            [key]: value,
            colorOverrides: normalizeUiColorOverrides([...uiSettings.colorOverrides, key]),
        } as Partial<StorefrontUiSettings>);
    };

    const resetOverrideColor = (key: UiColorOverrideKey) => {
        updateUiSettingsDraft({
            [key]: getInheritedUiColors(uiSettings)[key],
            colorOverrides: uiSettings.colorOverrides.filter((overrideKey) => overrideKey !== key),
        } as Partial<StorefrontUiSettings>);
    };

    const resetAllColorOverrides = () => {
        updateUiSettingsDraft({ colorOverrides: [] });
    };

    const inheritedColor = (label: string, colorKey: UiColorOverrideKey) => (
        <div className="pf-appearance-control-field">
            <InheritedColorSetting
                label={label}
                colorKey={colorKey}
                uiSettings={uiSettings}
                onChange={updateOverrideColor}
                onReset={resetOverrideColor}
            />
        </div>
    );

    let panelMarkup: ReactNode;

    if (activePanel === "global") {
        panelMarkup = (
            <BlockStack gap="300">
                <BlockStack gap="100">
                    <Text as="h3" variant="headingSm">Global style</Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                        These colors feed the rest of the proxy UI until an individual setting is customized.
                    </Text>
                </BlockStack>
                <div className="pf-appearance-control-field"><ColorSetting label="Primary color" value={uiSettings.globalPrimaryColor} onChange={(value) => updateGlobalColor("globalPrimaryColor", value)} /></div>
                <div className="pf-appearance-control-field"><ColorSetting label="Secondary color" value={uiSettings.globalSecondaryColor} onChange={(value) => updateGlobalColor("globalSecondaryColor", value)} /></div>
                <div className="pf-appearance-control-field"><ColorSetting label="Accent color" value={uiSettings.globalAccentColor} onChange={(value) => updateGlobalColor("globalAccentColor", value)} /></div>
                <div className="pf-appearance-control-field"><ColorSetting label="Surface color" value={uiSettings.globalSurfaceColor} onChange={(value) => updateGlobalColor("globalSurfaceColor", value)} /></div>
                <div className="pf-appearance-control-field"><ColorSetting label="Subtle background" value={uiSettings.globalSubtleBackgroundColor} onChange={(value) => updateGlobalColor("globalSubtleBackgroundColor", value)} /></div>
                <div className="pf-appearance-control-field"><ColorSetting label="Text color" value={uiSettings.globalTextColor} onChange={(value) => updateGlobalColor("globalTextColor", value)} /></div>
                <div className="pf-appearance-control-field"><ColorSetting label="Inverse text" value={uiSettings.globalInverseTextColor} onChange={(value) => updateGlobalColor("globalInverseTextColor", value)} /></div>
                <div className="pf-appearance-control-field"><ColorSetting label="Muted text" value={uiSettings.globalMutedTextColor} onChange={(value) => updateGlobalColor("globalMutedTextColor", value)} /></div>
                <div className="pf-appearance-control-field"><ColorSetting label="Border color" value={uiSettings.globalBorderColor} onChange={(value) => updateGlobalColor("globalBorderColor", value)} /></div>
                <div className="pf-appearance-control-field"><ColorSetting label="Review star color" value={uiSettings.globalReviewStarColor} onChange={(value) => updateGlobalColor("globalReviewStarColor", value)} /></div>
                {uiSettings.colorOverrides.length > 0 ? (
                    <Button variant="tertiary" onClick={resetAllColorOverrides}>Reset all color overrides</Button>
                ) : null}
            </BlockStack>
        );
    } else if (activePanel === "vehicle-search") {
        panelMarkup = (
            <BlockStack gap="300">
                <Text as="h3" variant="headingSm">Vehicle search bar</Text>
                <div className="pf-appearance-control-field">
                    <TextField
                        label="Content width"
                        autoComplete="off"
                        type="number"
                        min={900}
                        max={2400}
                        value={String(uiSettings.searchContentWidth)}
                        onChange={(value) => updateUiNumberSetting("searchContentWidth", value)}
                        suffix="px"
                    />
                </div>
                <div className="pf-appearance-control-field">
                    <TextField
                        label="Border radius"
                        autoComplete="off"
                        type="number"
                        min={0}
                        max={30}
                        value={String(uiSettings.searchBorderRadius)}
                        onChange={(value) => updateUiNumberSetting("searchBorderRadius", value)}
                        suffix="px"
                    />
                </div>
                <div className="pf-appearance-control-field">
                    <TextField
                        label="Padding"
                        autoComplete="off"
                        type="number"
                        min={0}
                        max={80}
                        value={String(uiSettings.searchPadding)}
                        onChange={(value) => updateUiNumberSetting("searchPadding", value)}
                        suffix="px"
                    />
                </div>
                {inheritedColor("Search bar background", "searchBarBackground")}
                {inheritedColor("Selected vehicle background", "selectedVehicleBackground")}
                {inheritedColor("Selected vehicle text", "selectedVehicleText")}
                {inheritedColor("Search button", "searchButtonBackground")}
                {inheritedColor("Search button text", "searchButtonText")}
                {inheritedColor("Clear button", "clearButtonBackground")}
                {inheritedColor("Clear button text", "clearButtonText")}
                {inheritedColor("Change button", "changeButtonBackground")}
                {inheritedColor("Change button text", "changeButtonText")}
            </BlockStack>
        );
    } else if (activePanel === "results-layout") {
        panelMarkup = (
            <BlockStack gap="300">
                <Text as="h3" variant="headingSm">Results layout</Text>
                <div className="pf-appearance-control-field">
                    <Select
                        label="Sort products"
                        options={resultsSortOptions}
                        value={uiSettings.resultsSortBy}
                        onChange={(value) => updateUiSettingsDraft({ resultsSortBy: value as StorefrontResultsSortMode })}
                    />
                </div>
                <div className="pf-appearance-control-field">
                    <Select
                        label="Default view"
                        options={resultsViewOptions}
                        value={uiSettings.defaultView}
                        onChange={(value) => updateUiSettingsDraft({ defaultView: value as StorefrontResultsViewMode })}
                    />
                </div>
                <div className="pf-appearance-control-field">
                    <TextField
                        label="Content width"
                        autoComplete="off"
                        type="number"
                        min={900}
                        max={1600}
                        value={String(uiSettings.resultsContentWidth)}
                        onChange={(value) => updateUiNumberSetting("resultsContentWidth", value)}
                        suffix="px"
                    />
                </div>
                <div className="pf-appearance-control-field">
                    <TextField
                        label="Desktop columns"
                        autoComplete="off"
                        type="number"
                        min={2}
                        max={6}
                        value={String(uiSettings.productsPerRowDesktop)}
                        onChange={(value) => updateUiNumberSetting("productsPerRowDesktop", value)}
                    />
                </div>
                <div className="pf-appearance-control-field">
                    <TextField
                        label="Tablet columns"
                        autoComplete="off"
                        type="number"
                        min={2}
                        max={5}
                        value={String(uiSettings.productsPerRowTablet)}
                        onChange={(value) => updateUiNumberSetting("productsPerRowTablet", value)}
                    />
                </div>
                <div className="pf-appearance-control-field">
                    <TextField
                        label="Mobile columns"
                        autoComplete="off"
                        type="number"
                        min={1}
                        max={4}
                        value={String(uiSettings.productsPerRowMobile)}
                        onChange={(value) => updateUiNumberSetting("productsPerRowMobile", value)}
                    />
                </div>
                <div className="pf-appearance-control-field">
                    <TextField
                        label="Image height"
                        autoComplete="off"
                        type="number"
                        min={160}
                        max={360}
                        value={String(uiSettings.cardImageHeight)}
                        onChange={(value) => updateUiNumberSetting("cardImageHeight", value)}
                        suffix="px"
                    />
                </div>
                <div className="pf-appearance-control-field">
                    <Select
                        label="Title max lines"
                        options={titleMaxLinesOptions}
                        value={String(uiSettings.titleMaxLines)}
                        onChange={(value) => updateUiNumberSetting("titleMaxLines", value)}
                    />
                </div>
                <div className="pf-appearance-control-field">
                    <TextField
                        label="Minimum card width"
                        autoComplete="off"
                        type="number"
                        min={180}
                        max={320}
                        value={String(uiSettings.minCardWidth)}
                        onChange={(value) => updateUiNumberSetting("minCardWidth", value)}
                        suffix="px"
                    />
                </div>
                <Checkbox label="Auto-fit card widths" checked={uiSettings.dynamicCardSizing} onChange={(checked) => updateUiSettingsDraft({ dynamicCardSizing: checked })} />
                <Checkbox label="Enable grid/list toggle" checked={uiSettings.enableViewToggle} onChange={(checked) => updateUiSettingsDraft({ enableViewToggle: checked })} />
                <Checkbox label="Show View as label" checked={uiSettings.showViewAsLabel} onChange={(checked) => updateUiSettingsDraft({ showViewAsLabel: checked })} />
                <Checkbox label="Show group selector" checked={uiSettings.showGroupSelector} onChange={(checked) => updateUiSettingsDraft({ showGroupSelector: checked })} />
                <Checkbox label="Show scroll to top" checked={uiSettings.showScrollTop} onChange={(checked) => updateUiSettingsDraft({ showScrollTop: checked })} />
                <Checkbox label="Sticky results navigation" checked={uiSettings.stickyGroupHeadings} onChange={(checked) => updateUiSettingsDraft({ stickyGroupHeadings: checked })} />
                {uiSettings.stickyGroupHeadings ? (
                    <div className="pf-appearance-control-field">
                        <TextField
                            label="Sticky top offset"
                            autoComplete="off"
                            type="number"
                            min={0}
                            max={160}
                            value={String(uiSettings.stickyHeadingOffset)}
                            onChange={(value) => updateUiNumberSetting("stickyHeadingOffset", value)}
                            suffix="px"
                        />
                    </div>
                ) : null}
            </BlockStack>
        );
    } else if (activePanel === "product-cards") {
        panelMarkup = (
            <BlockStack gap="300">
                <Text as="h3" variant="headingSm">Product cards</Text>
                <Checkbox label="Show fitment badges" checked={uiSettings.showFitmentBadges} onChange={(checked) => updateUiSettingsDraft({ showFitmentBadges: checked })} />
                <Checkbox label="Show stock status" checked={uiSettings.showStockStatus} onChange={(checked) => updateUiSettingsDraft({ showStockStatus: checked })} />
                <Checkbox label="Show vendor" checked={uiSettings.showVendor} onChange={(checked) => updateUiSettingsDraft({ showVendor: checked })} />
                <Checkbox label="Show SKU" checked={uiSettings.showSku} onChange={(checked) => updateUiSettingsDraft({ showSku: checked })} />
                <Checkbox label="Show fitment notes" checked={uiSettings.showFitmentNotes} onChange={(checked) => updateUiSettingsDraft({ showFitmentNotes: checked })} />
                <Checkbox label="Enable hover animation" checked={uiSettings.hoverAnimation} onChange={(checked) => updateUiSettingsDraft({ hoverAnimation: checked })} />
                <Checkbox label="Show review count" checked={uiSettings.showReviewCount} onChange={(checked) => updateUiSettingsDraft({ showReviewCount: checked })} />
                <Checkbox label="Disable default result styles" checked={uiSettings.disableResultStyles} onChange={(checked) => updateUiSettingsDraft({ disableResultStyles: checked })} />
                <div className="pf-appearance-control-field">
                    <Select
                        label="Product reviews"
                        options={reviewDisplayOptions}
                        value={uiSettings.reviewDisplayMode}
                        onChange={(value) => updateUiSettingsDraft({ reviewDisplayMode: value as StorefrontReviewDisplayMode })}
                    />
                </div>
                <div className="pf-appearance-control-field">
                    <Select
                        label="Product button"
                        options={buttonBehaviorOptions}
                        value={uiSettings.buttonBehavior}
                        onChange={(value) => updateUiSettingsDraft({ buttonBehavior: value as StorefrontButtonBehavior })}
                    />
                </div>
            </BlockStack>
        );
    } else {
        panelMarkup = (
            <BlockStack gap="300">
                <Text as="h3" variant="headingSm">Result colors</Text>
                {inheritedColor("Heading background", "headingBackground")}
                {inheritedColor("Heading text", "headingText")}
                {inheritedColor("Card background", "cardBackground")}
                {inheritedColor("Card text", "cardText")}
                {inheritedColor("Button background", "buttonBackground")}
                {inheritedColor("Button text", "buttonText")}
                {inheritedColor("Exact fit badge", "exactBadgeColor")}
                {inheritedColor("Universal fit badge", "universalBadgeColor")}
                {inheritedColor("Hover border", "hoverBorderColor")}
                {inheritedColor("Review stars", "reviewStarColor")}
                {inheritedColor("Empty review stars", "reviewEmptyStarColor")}
            </BlockStack>
        );
    }

    return (
        <BlockStack gap="500">
            <Select
                label="Edit"
                options={appearancePanelOptions}
                value={activePanel}
                onChange={(value) => setActivePanel(value as AppearancePanelKey)}
            />
            <Divider />
            {panelMarkup}
        </BlockStack>
    );
}

function AppearancePreview({ uiSettings }: { uiSettings: StorefrontUiSettings }) {
    const previewStyle = {
        "--pf-preview-search-width": `${uiSettings.searchContentWidth}px`,
        "--pf-preview-results-width": `${uiSettings.resultsContentWidth}px`,
        "--pf-preview-radius": `${uiSettings.searchBorderRadius}px`,
        "--pf-preview-padding": `${uiSettings.searchPadding}px`,
        "--pf-preview-search-bg": uiSettings.searchBarBackground,
        "--pf-preview-search-button": uiSettings.searchButtonBackground,
        "--pf-preview-search-button-text": uiSettings.searchButtonText,
        "--pf-preview-clear-button": uiSettings.clearButtonBackground,
        "--pf-preview-clear-button-text": uiSettings.clearButtonText,
        "--pf-preview-selected-bg": uiSettings.selectedVehicleBackground,
        "--pf-preview-selected-text": uiSettings.selectedVehicleText,
        "--pf-preview-change-button": uiSettings.changeButtonBackground,
        "--pf-preview-change-button-text": uiSettings.changeButtonText,
        "--pf-preview-heading-bg": uiSettings.headingBackground,
        "--pf-preview-heading-text": uiSettings.headingText,
        "--pf-preview-card-bg": uiSettings.cardBackground,
        "--pf-preview-card-text": uiSettings.cardText,
        "--pf-preview-button-bg": uiSettings.buttonBackground,
        "--pf-preview-button-text": uiSettings.buttonText,
        "--pf-preview-exact": uiSettings.exactBadgeColor,
        "--pf-preview-universal": uiSettings.universalBadgeColor,
        "--pf-preview-hover": uiSettings.hoverBorderColor,
        "--pf-preview-review-star": uiSettings.reviewStarColor,
        "--pf-preview-review-empty-star": uiSettings.reviewEmptyStarColor,
        "--pf-preview-columns": Math.min(Math.max(uiSettings.productsPerRowDesktop, 1), 3),
        "--pf-preview-image-height": `${Math.max(96, Math.round(uiSettings.cardImageHeight * 0.58))}px`,
    } as CSSProperties;

    const products = [
        { title: "Disc Brake B-1", price: "$725.00", badge: "Exact fit", exact: true, meta: "Front brake kit" },
        { title: "Performance Pad Set", price: "$129.00", badge: "Exact fit", exact: true, meta: "Ceramic compound" },
        { title: "Universal Cleaner", price: "$24.00", badge: "Universal", exact: false, meta: "Shop supply" },
    ];

    return (
        <div className={`pf-appearance-preview ${uiSettings.defaultView === "list" ? "pf-appearance-preview--list" : ""}`} style={previewStyle}>
            <div className="pf-appearance-preview__browser">
                <div className="pf-appearance-preview__selected">
                    <strong>2027 Audi Q7 Premium</strong>
                    <button type="button">Change motorcycle</button>
                </div>
                <div className="pf-appearance-preview__search">
                    {["2027", "Audi", "Q7", "Premium"].map((value) => (
                        <div key={value} className="pf-appearance-preview__field">{value}</div>
                    ))}
                    <button type="button" className="pf-appearance-preview__search-button">Search</button>
                    <button type="button" className="pf-appearance-preview__clear-button">Clear</button>
                </div>
                <section className="pf-appearance-preview__results">
                    <header className="pf-appearance-preview__heading">
                        <h2>Products</h2>
                        <span>2027 Audi Q7 Premium</span>
                    </header>
                    <div className="pf-appearance-preview__grid">
                        {products.map((product, index) => (
                            <article key={product.title} className="pf-appearance-preview__card">
                                {uiSettings.showFitmentBadges ? (
                                    <span className={product.exact ? "pf-appearance-preview__badge pf-appearance-preview__badge--exact" : "pf-appearance-preview__badge pf-appearance-preview__badge--universal"}>
                                        {product.badge}
                                    </span>
                                ) : null}
                                <div className="pf-appearance-preview__image" aria-hidden="true">
                                    <span />
                                </div>
                                <div className="pf-appearance-preview__content">
                                    <h3>{product.title}</h3>
                                    {uiSettings.showVendor || uiSettings.showSku ? (
                                        <p>
                                            {uiSettings.showVendor ? "PartFits" : ""}
                                            {uiSettings.showVendor && uiSettings.showSku ? " / " : ""}
                                            {uiSettings.showSku ? `SKU-${index + 101}` : ""}
                                        </p>
                                    ) : null}
                                    {uiSettings.showFitmentNotes ? <p>{product.meta}</p> : null}
                                    {uiSettings.showStockStatus ? <p>In stock</p> : null}
                                    {uiSettings.reviewDisplayMode !== "none" ? (
                                        <div className="pf-appearance-preview__stars">
                                            <span>★★★★★</span>
                                            {uiSettings.showReviewCount ? <small>18</small> : null}
                                        </div>
                                    ) : null}
                                    <strong>{product.price}</strong>
                                    <button type="button">{uiSettings.buttonBehavior === "add-to-cart" ? "Add to cart" : "View product"}</button>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
}

const settingsSectionKeys = [
    "search-results",
    "selector-fields",
    "storefront-text",
    "appearance",
    "reference-data",
    "attributes",
    "data-refresh",
    "integrations",
    "advanced",
    "plan",
];
const APPEARANCE_SECTION_INDEX = settingsSectionKeys.indexOf("appearance");
const SELECTOR_FIELDS_SECTION_INDEX = settingsSectionKeys.indexOf("selector-fields");
const REFERENCE_DATA_SECTION_INDEX = settingsSectionKeys.indexOf("reference-data");
const ATTRIBUTES_SECTION_INDEX = settingsSectionKeys.indexOf("attributes");
const DATA_REFRESH_SECTION_INDEX = settingsSectionKeys.indexOf("data-refresh");
const INTEGRATIONS_SECTION_INDEX = settingsSectionKeys.indexOf("integrations");
const STOREFRONT_TEXT_SECTION_INDEX = settingsSectionKeys.indexOf("storefront-text");
const ADVANCED_SECTION_INDEX = settingsSectionKeys.indexOf("advanced");
const PLAN_SECTION_INDEX = settingsSectionKeys.indexOf("plan");
const referenceDataSectionDescription = "Choose which vehicle and fitment data sources the app can use for selectors, Product Guides, and product mapping.";
const attributesSectionDescription = "Connect Shopify metafields so the app can use their values in Product Guides, filters, and storefront display.";
const dataRefreshSectionDescription = "Sync the latest Shopify products, collections, tags, and connected attribute data into the app.";
const integrationsSectionDescription = "Connect vendor accounts used for product data, fitment data, inventory, and fulfillment.";
const planSectionDescription = "Review your current PartFits plan and compare the capabilities available in each tier.";

const getSettingsSectionIndex = (search: string) => {
    const section = new URLSearchParams(search).get("section");
    const index = settingsSectionKeys.indexOf(section || "");
    return index >= 0 ? index : 0;
};

const StorefrontPage: React.FC = () => {
    const { settingStore } = useStore();
    const navigate = useNavigate();
    const location = useLocation();
    const {
        currentSettings: settings,
        loadingInitial,
        loadingUpdate,
        loadSettings,
        themeSettings,
        loadingThemeSettings,
        loadThemeSettings,
    } = settingStore;

    const [settingsLoaded, setSettingsLoaded] = useState(false);
    const [selectedTab, setSelectedTab] = useState(() => getSettingsSectionIndex(location.search));
    const [settingsMobileNavOpen, setSettingsMobileNavOpen] = useState(false);
    const [settingsEditorOpen, setSettingsEditorOpen] = useState(true);
    const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
    const allowSettingsCloseRef = useRef(false);
    const allowConfirmCloseRef = useRef(false);
    const [saving, setSaving] = useState(false);
    const [persistentFilteringChecked, setPersistentFilteringChecked] = useState(false);
    const [submodelEnabled, setSubmodelEnabled] = useState(false);
    const [themeDraft, setThemeDraft] = useState<StorefrontThemeSettings | undefined>(undefined);
    const [legacySettingsOpen, setLegacySettingsOpen] = useState(false);
    const [filterPatternOverrideOpen, setFilterPatternOverrideOpen] = useState(false);
    const [resultsLoading, setResultsLoading] = useState(true);
    const [resultsDraft, setResultsDraft] = useState<StorefrontResultsSettings>();
    const [resultsBaseline, setResultsBaseline] = useState<StorefrontResultsSettings>();
    const [resultsError, setResultsError] = useState<string>();
    const [resultsSuccess, setResultsSuccess] = useState<string>();
    const [groupModalOpen, setGroupModalOpen] = useState(false);
    const [editingGroupIndex, setEditingGroupIndex] = useState<number>();
    const [groupDraft, setGroupDraft] = useState<StorefrontCuratedGroup>();
    const [collectionFilter, setCollectionFilter] = useState("");
    const [productTypeSearch, setProductTypeSearch] = useState("");
    const [collectionGroupSearch, setCollectionGroupSearch] = useState("");
    const [fitmentFieldsMounted, setFitmentFieldsMounted] = useState(false);
    const [fitmentFieldsEditor, setFitmentFieldsEditor] = useState<FitmentFieldsEditorStatus>({
        hasChanges: false,
        loading: false,
        saving: false,
    });
    const [attributesMounted, setAttributesMounted] = useState(false);
    const [attributeEditor, setAttributeEditor] = useState<MetafieldAttributesEditorStatus>({
        hasChanges: false,
        loading: false,
        saving: false,
    });
    const [integrationsMounted, setIntegrationsMounted] = useState(false);
    const [integrationEditor, setIntegrationEditor] = useState<IntegrationEditorStatus>({
        hasChanges: false,
        loading: false,
        saving: false,
    });
    const [translationsMounted, setTranslationsMounted] = useState(false);
    const [translationEditor, setTranslationEditor] = useState<StorefrontTranslationsEditorStatus>({
        hasChanges: false,
        loading: false,
        saving: false,
    });
    const [storefrontTextHeaderActions, setStorefrontTextHeaderActions] = useState<ReactNode | null>(null);
    const appProxyResultsEnabled = resultsDraft?.renderingMode === "AppProxyResults";

    const openShopifySyncSettings = useCallback(() => {
        setSelectedTab(DATA_REFRESH_SECTION_INDEX);
        setSettingsMobileNavOpen(false);
    }, []);

    const openProductAttributesSettings = useCallback(() => {
        setSelectedTab(ATTRIBUTES_SECTION_INDEX);
        setSettingsMobileNavOpen(false);
    }, []);

    const openFitmentDataSourcesSettings = useCallback(() => {
        setSelectedTab(REFERENCE_DATA_SECTION_INDEX);
        setSettingsMobileNavOpen(false);
    }, []);

    const openVendorIntegrationsSettings = useCallback(() => {
        setSelectedTab(INTEGRATIONS_SECTION_INDEX);
        setSettingsMobileNavOpen(false);
    }, []);

    const settingsSections = [
        { label: "Search & results", icon: SearchIcon },
        { label: "Selector fields", icon: FormsIcon },
        { label: "Storefront text", icon: LanguageTranslateIcon },
        { label: "Appearance", icon: PaintBrushRoundIcon },
        { label: "Fitment data sources", icon: DatabaseIcon },
        { label: "Product attributes", icon: MetafieldsIcon },
        { label: "Shopify sync", icon: RefreshIcon },
        { label: "Vendor integrations", icon: ConnectIcon },
        { label: "Advanced", icon: SettingsIcon },
        { label: "Plan", icon: PlanIcon },
    ];

    useEffect(() => {
        const fetchSettings = async () => {
            await loadSettings();
            setSettingsLoaded(true);
        };

        if (!settingsLoaded) fetchSettings();
    }, [settingsLoaded, loadSettings]);

    useEffect(() => {
        loadThemeSettings();
    }, [loadThemeSettings]);

    useEffect(() => {
        if (resultsLoading || selectedTab !== APPEARANCE_SECTION_INDEX || appProxyResultsEnabled) {
            return;
        }

        setSelectedTab(0);
        setSettingsMobileNavOpen(false);
    }, [appProxyResultsEnabled, resultsLoading, selectedTab]);

    useEffect(() => {
        setSelectedTab(getSettingsSectionIndex(location.search));
        setSettingsMobileNavOpen(false);
    }, [location.search]);

    useEffect(() => {
        if (selectedTab === SELECTOR_FIELDS_SECTION_INDEX || selectedTab === REFERENCE_DATA_SECTION_INDEX) {
            setFitmentFieldsMounted(true);
        }

        if (selectedTab === ATTRIBUTES_SECTION_INDEX) {
            setAttributesMounted(true);
        }

        if (selectedTab === INTEGRATIONS_SECTION_INDEX) {
            setIntegrationsMounted(true);
        }

        if (selectedTab === STOREFRONT_TEXT_SECTION_INDEX) {
            setTranslationsMounted(true);
        }
    }, [selectedTab]);

    useEffect(() => {
        if (settings) {
            setPersistentFilteringChecked(!!settings.searchEnablePersistentFiltering);
            setSubmodelEnabled(settings.submodelEnabled ?? false);
        }
    }, [settings]);

    useEffect(() => {
        if (themeSettings) {
            setThemeDraft(themeSettings);
        }
    }, [themeSettings]);

    useEffect(() => {
        const loadResults = async () => {
            setResultsLoading(true);
            setResultsError(undefined);

            try {
                const settings = await agent.Settings.storefrontResults();
                const nextSettings = cloneResultsSettings(withResultsDefaults(settings));
                setResultsDraft(nextSettings);
                setResultsBaseline(cloneResultsSettings(nextSettings));
            } catch (err: any) {
                setResultsError(err?.message ?? "Failed to load storefront settings.");
            } finally {
                setResultsLoading(false);
            }
        };

        loadResults();
    }, []);

    const updateThemeDraft = useCallback((key: keyof StorefrontThemeSettings, value: string | boolean | number) => {
        setThemeDraft((current) => current ? ({ ...current, [key]: value } as StorefrontThemeSettings) : current);
    }, []);

    const updateSearchDestinationMode = useCallback((mode: SearchDestinationMode) => {
        setThemeDraft((current) => {
            if (!current) return current;

            return {
                ...current,
                redirectToResults: mode === "ResultsPage",
            };
        });
    }, []);

    const updateResultsPageLocationMode = useCallback((mode: ResultsPageLocationMode) => {
        setThemeDraft((current) => {
            if (!current) return current;

            return {
                ...current,
                customRedirectEnabled: mode === "Custom",
            };
        });
    }, []);

    const updateResultsDraft = useCallback((changes: Partial<StorefrontResultsSettings>) => {
        setResultsSuccess(undefined);
        setResultsDraft((current) => current ? ({ ...current, ...changes }) : current);
    }, []);

    const updateUiSettingsDraft = useCallback((changes: Partial<StorefrontUiSettings>) => {
        setResultsSuccess(undefined);
        setResultsDraft((current) => current ? ({
            ...current,
            uiSettings: {
                ...withUiDefaults(current.uiSettings),
                ...changes,
            },
        }) : current);
    }, []);

    const updateUiNumberSetting = useCallback((key: keyof StorefrontUiSettings, value: string) => {
        const parsed = Number.parseInt(value, 10);
        updateUiSettingsDraft({ [key]: Number.isFinite(parsed) ? parsed : 0 } as Partial<StorefrontUiSettings>);
    }, [updateUiSettingsDraft]);

    const updateRenderingMode = useCallback((mode: StorefrontRenderingMode) => {
        updateResultsDraft({ renderingMode: mode });

        if (mode === "RenderedResults" || mode === "AppProxyResults") {
            setThemeDraft((current) => {
                if (!current) return current;

                const nextPath = stripVehiclePlaceholderFromPath(current.redirectPath);
                return nextPath === current.redirectPath
                    ? current
                    : { ...current, redirectPath: nextPath };
            });
        }
    }, [updateResultsDraft]);

    const sortedGroups = useMemo(
        () => (resultsDraft?.curatedGroups ?? [])
            .map((group, originalIndex) => ({ group, originalIndex }))
            .sort((left, right) =>
                left.group.displayOrder - right.group.displayOrder || left.group.name.localeCompare(right.group.name)
            ),
        [resultsDraft?.curatedGroups]
    );

    const collectionMap = useMemo(
        () => new Map((resultsDraft?.availableCollections ?? []).map((collection) => [collection.id, collection])),
        [resultsDraft?.availableCollections]
    );

    const filteredCollections = useMemo(() => {
        const term = collectionFilter.trim().toLowerCase();
        const collections = resultsDraft?.availableCollections ?? [];

        if (!term) {
            return collections;
        }

        return collections.filter((collection) =>
            collection.title.toLowerCase().includes(term)
            || collection.handle.toLowerCase().includes(term)
        );
    }, [collectionFilter, resultsDraft?.availableCollections]);

    const filteredProductTypes = useMemo(() => {
        const term = productTypeSearch.trim().toLowerCase();
        const productTypes = resultsDraft?.availableProductTypes ?? [];

        if (!term) {
            return productTypes;
        }

        return productTypes.filter((productType) => productType.toLowerCase().includes(term));
    }, [productTypeSearch, resultsDraft?.availableProductTypes]);

    const filteredGroupCollections = useMemo(() => {
        const term = collectionGroupSearch.trim().toLowerCase();
        const collections = resultsDraft?.availableCollections ?? [];

        if (!term) {
            return collections;
        }

        return collections.filter((collection) =>
            collection.title.toLowerCase().includes(term)
            || collection.handle.toLowerCase().includes(term)
        );
    }, [collectionGroupSearch, resultsDraft?.availableCollections]);

    const toggleProductTypeFilterValue = useCallback((productType: string, checked: boolean) => {
        const currentValues = resultsDraft?.productTypeFilterValues ?? [];
        const nextValues = checked
            ? Array.from(new Set([...currentValues, productType])).sort((left, right) => left.localeCompare(right))
            : currentValues.filter((value) => value !== productType);

        updateResultsDraft({ productTypeFilterValues: nextValues });
    }, [resultsDraft?.productTypeFilterValues, updateResultsDraft]);

    const toggleCollectionFilterValue = useCallback((collectionId: number, checked: boolean) => {
        const currentIds = resultsDraft?.collectionFilterIds ?? [];
        const nextIds = checked
            ? Array.from(new Set([...currentIds, collectionId])).sort((left, right) => left - right)
            : currentIds.filter((id) => id !== collectionId);

        updateResultsDraft({ collectionFilterIds: nextIds });
    }, [resultsDraft?.collectionFilterIds, updateResultsDraft]);

    const baselineTheme = themeSettings || DEFAULT_THEME_SETTINGS;
    const hasThemeUnsavedChanges =
        !!themeDraft
        && themeSettingsSnapshot(themeDraft, persistentFilteringChecked, submodelEnabled)
            !== themeSettingsSnapshot(
                baselineTheme,
                !!settings?.searchEnablePersistentFiltering,
                settings?.submodelEnabled ?? false
            );
    const hasResultsUnsavedChanges = resultSettingsSnapshot(resultsDraft) !== resultSettingsSnapshot(resultsBaseline);
    const hasUnsavedChanges = hasThemeUnsavedChanges
        || hasResultsUnsavedChanges
        || fitmentFieldsEditor.hasChanges
        || attributeEditor.hasChanges
        || integrationEditor.hasChanges
        || translationEditor.hasChanges;
    const editorActionInProgress = saving
        || loadingUpdate
        || fitmentFieldsEditor.saving
        || attributeEditor.saving
        || integrationEditor.saving
        || translationEditor.saving;

    const handleSave = useCallback(async () => {
        const updates: Promise<unknown>[] = [];
        let savedResults: StorefrontResultsSettings | undefined;

        setSaving(true);
        setResultsError(undefined);
        setResultsSuccess(undefined);

        try {
            if (hasThemeUnsavedChanges && themeDraft) {
                const tagPrefixEnabled = settings?.useYmmPrefixForProductTags ?? themeDraft.tagPrefixEnabled ?? true;
                const nextSetting: UserSetting = {
                    searchEnablePersistentFiltering: persistentFilteringChecked,
                    inventoryAutoMapEnabled: settings?.inventoryAutoMapEnabled ?? false,
                    useYmmPrefixForProductTags: tagPrefixEnabled,
                    submodelEnabled,
                    inventoryLocationIds: settings?.inventoryLocationIds ?? [],
                };

                if (settings?.id !== undefined) {
                    nextSetting.id = settings.id;
                }

                const themePayload: StorefrontThemeSettings = {
                    ...themeDraft,
                    globalFilter: persistentFilteringChecked,
                    tagPrefixEnabled,
                    submodelEnabled,
                    renderingMode: resultsDraft?.renderingMode ?? themeDraft.renderingMode,
                    resultsReturnType: resultsDraft?.returnResultsAs ?? themeDraft.resultsReturnType,
                    resultsGroupingMode: resultsDraft?.groupResultsBy ?? themeDraft.resultsGroupingMode,
                    priceDisplayMode: resultsDraft?.priceDisplayMode ?? themeDraft.priceDisplayMode,
                    variantResultDisplayMode: resultsDraft?.variantResultDisplayMode ?? themeDraft.variantResultDisplayMode,
                };

                updates.push(settingStore.updateSettings(nextSetting));
                updates.push(settingStore.updateThemeSettings(themePayload));
            }

            if (hasResultsUnsavedChanges && resultsDraft) {
                updates.push(
                    agent.Settings.updateStorefrontResults({
                        ...resultsDraft,
                        curatedGroups: sortedGroups.map(({ group }) => group),
                    }).then((saved) => {
                        savedResults = saved;
                    })
                );
            }

            if (fitmentFieldsEditor.hasChanges && fitmentFieldsEditor.save) {
                updates.push(fitmentFieldsEditor.save());
            }

            if (attributeEditor.hasChanges && attributeEditor.save) {
                updates.push(attributeEditor.save());
            }

            if (integrationEditor.hasChanges && integrationEditor.save) {
                updates.push(integrationEditor.save());
            }

            if (translationEditor.hasChanges && translationEditor.save) {
                updates.push(translationEditor.save());
            }

            await Promise.all(updates);

            if (savedResults) {
                const nextSettings = cloneResultsSettings(withResultsDefaults(savedResults));
                setResultsDraft(nextSettings);
                setResultsBaseline(cloneResultsSettings(nextSettings));
                setResultsSuccess("Storefront results saved.");
            }
        } catch (err: any) {
            setResultsError(err?.message ?? "Failed to save storefront settings.");
        } finally {
            setSaving(false);
        }
    }, [
        fitmentFieldsEditor,
        hasResultsUnsavedChanges,
        hasThemeUnsavedChanges,
        attributeEditor,
        integrationEditor,
        persistentFilteringChecked,
        resultsDraft,
        settingStore,
        settings,
        sortedGroups,
        submodelEnabled,
        themeDraft,
        translationEditor,
    ]);

    const handleDiscard = useCallback(() => {
        setPersistentFilteringChecked(!!settings?.searchEnablePersistentFiltering);
        setSubmodelEnabled(settings?.submodelEnabled ?? false);
        setThemeDraft(themeSettings);

        if (resultsBaseline) {
            setResultsDraft(cloneResultsSettings(resultsBaseline));
        }

        setResultsError(undefined);
        setResultsSuccess(undefined);

        if (fitmentFieldsEditor.hasChanges) {
            fitmentFieldsEditor.discard?.();
        }

        if (attributeEditor.hasChanges) {
            attributeEditor.discard?.();
        }

        if (integrationEditor.hasChanges) {
            integrationEditor.discard?.();
        }

        if (translationEditor.hasChanges) {
            translationEditor.discard?.();
        }
    }, [attributeEditor, fitmentFieldsEditor, integrationEditor, resultsBaseline, settings, themeSettings, translationEditor]);

    const closeSettingsEditor = useCallback(() => {
        navigate(getLastAppRoute(), { replace: true });
    }, [navigate]);

    const handleSettingsEditorHide = useCallback(() => {
        if (allowSettingsCloseRef.current || !hasUnsavedChanges) {
            allowSettingsCloseRef.current = false;
            closeSettingsEditor();
            return;
        }

        setSettingsEditorOpen(false);
        setConfirmCloseOpen(true);
    }, [closeSettingsEditor, hasUnsavedChanges]);

    const stayInSettingsEditor = useCallback(() => {
        allowConfirmCloseRef.current = true;
        setConfirmCloseOpen(false);
        setSettingsEditorOpen(true);
    }, []);

    const leaveSettingsEditor = useCallback(() => {
        allowConfirmCloseRef.current = true;
        allowSettingsCloseRef.current = true;
        handleDiscard();
        setConfirmCloseOpen(false);
        closeSettingsEditor();
    }, [closeSettingsEditor, handleDiscard]);

    const handleConfirmCloseHide = useCallback(() => {
        setConfirmCloseOpen(false);

        if (allowConfirmCloseRef.current) {
            allowConfirmCloseRef.current = false;
            return;
        }

        setSettingsEditorOpen(true);
    }, []);

    const openAddGroupModal = useCallback(() => {
        setEditingGroupIndex(undefined);
        setGroupDraft(createBlankGroup((resultsDraft?.curatedGroups.length ?? 0) + 1));
        setCollectionFilter("");
        setGroupModalOpen(true);
    }, [resultsDraft?.curatedGroups.length]);

    const openEditGroupModal = useCallback((index: number) => {
        if (!resultsDraft?.curatedGroups[index]) return;
        setEditingGroupIndex(index);
        setGroupDraft({
            ...resultsDraft.curatedGroups[index],
            collectionIds: [...resultsDraft.curatedGroups[index].collectionIds],
        });
        setCollectionFilter("");
        setGroupModalOpen(true);
    }, [resultsDraft?.curatedGroups]);

    const closeGroupModal = useCallback(() => {
        setGroupModalOpen(false);
        setEditingGroupIndex(undefined);
        setGroupDraft(undefined);
        setCollectionFilter("");
    }, []);

    const saveGroupDraft = useCallback(() => {
        if (!groupDraft || !resultsDraft) return;

        const normalizedGroup = {
            ...groupDraft,
            name: groupDraft.name.trim(),
            displayOrder: Number(groupDraft.displayOrder) || (resultsDraft.curatedGroups.length + 1),
            collectionIds: Array.from(new Set(groupDraft.collectionIds)),
        };

        if (!normalizedGroup.name) return;

        setResultsDraft((current) => {
            if (!current) return current;

            const nextGroups = [...current.curatedGroups];
            if (editingGroupIndex === undefined) {
                nextGroups.push(normalizedGroup);
            } else {
                nextGroups[editingGroupIndex] = normalizedGroup;
            }

            return { ...current, curatedGroups: nextGroups };
        });
        setResultsSuccess(undefined);
        closeGroupModal();
    }, [closeGroupModal, editingGroupIndex, groupDraft, resultsDraft]);

    const deleteGroup = useCallback((index: number) => {
        setResultsDraft((current) => {
            if (!current) return current;

            return {
                ...current,
                curatedGroups: current.curatedGroups.filter((_, groupIndex) => groupIndex !== index),
            };
        });
        setResultsSuccess(undefined);
    }, []);

    const updateGroupDraft = useCallback((changes: Partial<StorefrontCuratedGroup>) => {
        setGroupDraft((current) => current ? ({ ...current, ...changes }) : current);
    }, []);

    const toggleCollection = useCallback((collectionId: number, checked: boolean) => {
        setGroupDraft((current) => {
            if (!current) return current;

            const selectedIds = new Set(current.collectionIds);
            if (checked) {
                selectedIds.add(collectionId);
            } else {
                selectedIds.delete(collectionId);
            }

            return { ...current, collectionIds: Array.from(selectedIds) };
        });
    }, []);

    if (!settingsLoaded || loadingInitial) {
        return (
            <SkeletonPage title="Settings">
                <Layout>
                    <Layout.Section>
                        <Card>
                            <Box padding="400">
                                <SkeletonBodyText />
                            </Box>
                        </Card>
                    </Layout.Section>
                </Layout>
            </SkeletonPage>
        );
    }

    const selectedCollectionIds = new Set(groupDraft?.collectionIds ?? []);
    const selectedProductTypeFilterValues = new Set(resultsDraft?.productTypeFilterValues ?? []);
    const selectedCollectionFilterIds = new Set(resultsDraft?.collectionFilterIds ?? []);
    const canSaveGroup = !!groupDraft?.name.trim();
    const searchDestinationMode = getSearchDestinationMode(themeDraft);
    const resultsPageLocationMode = getResultsPageLocationMode(themeDraft);
    const shopifyFilteringMethod = getShopifyFilteringMethod(themeDraft);
    const loadingPrimarySettings = loadingThemeSettings || !themeDraft || resultsLoading;
    const renderedResultsEnabled = resultsDraft?.renderingMode === "RenderedResults";
    const appRenderedResultsEnabled = renderedResultsEnabled || appProxyResultsEnabled;
    const resultsSettingsTitle = appProxyResultsEnabled
        ? "Product results"
        : renderedResultsEnabled
            ? "Results page"
            : "Fallback results page";
    const resultsSettingsDescription = appProxyResultsEnabled
        ? "Control how matching products are presented on the hosted PartFits results page."
        : renderedResultsEnabled
            ? "Choose the Shopify destination and control how matching products are presented."
            : "Choose where searches land when they cannot remain on the current collection.";
    const rawUiSettings = withUiDefaults(resultsDraft?.uiSettings);
    const uiSettings = getEffectiveUiSettings(rawUiSettings);
    const appearanceSelected = selectedTab === APPEARANCE_SECTION_INDEX;
    const selectedSection = appearanceSelected && !appProxyResultsEnabled
        ? settingsSections[0]
        : settingsSections[selectedTab] ?? settingsSections[0];
    const renderSettingsNavButtons = (closeAfterSelect = false) => (
        <BlockStack gap="100">
            {settingsSections.map((section, index) => {
                if (index === APPEARANCE_SECTION_INDEX && !appProxyResultsEnabled) {
                    return null;
                }

                return (
                    <Button
                        key={`${closeAfterSelect ? "mobile" : "desktop"}-${section.label}`}
                        fullWidth
                        icon={section.icon}
                        textAlign="left"
                        variant="tertiary"
                        pressed={selectedTab === index}
                        onClick={() => {
                            setSelectedTab(index);
                            if (closeAfterSelect) {
                                setSettingsMobileNavOpen(false);
                            }
                        }}
                        accessibilityLabel={`${section.label} settings`}
                    >
                        {section.label}
                    </Button>
                );
            })}
        </BlockStack>
    );

    return (
        <>
            {!settingsEditorOpen ? (
                <Page
                    title="Settings"
                    primaryAction={{
                        content: "Open settings",
                        onAction: () => setSettingsEditorOpen(true),
                    }}
                >
                    <Layout>
                        <Layout.Section>
                            <SettingsCard
                                title="PartFits settings"
                                description="Edit search behavior, fitment fields, fitment data sources, attributes, Shopify sync, vendor integrations, translations, and advanced storefront options."
                            >
                                <Button onClick={() => setSettingsEditorOpen(true)}>Open settings</Button>
                            </SettingsCard>
                        </Layout.Section>
                    </Layout>
                </Page>
            ) : null}

            <AppBridgeModal
                id="storefront-settings-editor"
                open={settingsEditorOpen}
                onHide={handleSettingsEditorHide}
                variant="max"
            >
                <div className="pf-settings-editor" aria-label="PartFits settings">
                    <div className="pf-settings-editor__frame">
                        <div className={`pf-settings-editor__body${appearanceSelected ? " pf-settings-editor__body--appearance" : ""}`}>
                            <aside className="pf-settings-editor__sidebar" aria-label="Settings sections">
                                <Card padding="0">
                                    <div className="pf-settings-editor__nav-card-header">
                                        <Box padding="400">
                                            <Text as="h1" variant="headingMd">Settings</Text>
                                        </Box>
                                    </div>
                                    <Box padding="300">
                                        <BlockStack gap="100">
                                            {renderSettingsNavButtons()}
                                        </BlockStack>
                                    </Box>
                                </Card>
                            </aside>
                            <main className={`pf-settings-editor__content${appearanceSelected ? " pf-settings-editor__content--appearance" : ""}`}>
                                <div className="pf-settings-editor__section-title">
                                    <div className="pf-settings-editor__section-title-inner">
                                        <div className="pf-settings-editor__mobile-nav-trigger">
                                            <Button
                                                icon={MenuIcon}
                                                variant="tertiary"
                                                pressed={settingsMobileNavOpen}
                                                onClick={() => setSettingsMobileNavOpen((open) => !open)}
                                                accessibilityLabel="Open settings navigation"
                                                ariaExpanded={settingsMobileNavOpen}
                                                ariaControls="storefront-settings-mobile-nav"
                                            />
                                        </div>
                                        <div className="pf-settings-editor__section-icon" aria-hidden="true">
                                            <Icon source={selectedSection.icon} tone="base" />
                                        </div>
                                        <div className="pf-settings-editor__section-copy">
                                            <h1 className="pf-settings-editor__section-heading">{selectedSection.label}</h1>
                                            {selectedTab === REFERENCE_DATA_SECTION_INDEX ? (
                                                <Text as="p" variant="bodyMd" tone="subdued">
                                                    {referenceDataSectionDescription}
                                                </Text>
                                            ) : selectedTab === ATTRIBUTES_SECTION_INDEX ? (
                                                <Text as="p" variant="bodyMd" tone="subdued">
                                                    {attributesSectionDescription}
                                                </Text>
                                            ) : selectedTab === DATA_REFRESH_SECTION_INDEX ? (
                                                <Text as="p" variant="bodyMd" tone="subdued">
                                                    {dataRefreshSectionDescription}
                                                </Text>
                                            ) : selectedTab === INTEGRATIONS_SECTION_INDEX ? (
                                                <Text as="p" variant="bodyMd" tone="subdued">
                                                    {integrationsSectionDescription}
                                                </Text>
                                            ) : selectedTab === PLAN_SECTION_INDEX ? (
                                                <Text as="p" variant="bodyMd" tone="subdued">
                                                    {planSectionDescription}
                                                </Text>
                                            ) : null}
                                        </div>
                                    </div>
                                    {selectedTab === STOREFRONT_TEXT_SECTION_INDEX && storefrontTextHeaderActions ? (
                                        <div className="pf-settings-editor__section-actions">
                                            {storefrontTextHeaderActions}
                                        </div>
                                    ) : null}
                                </div>
                                {settingsMobileNavOpen ? (
                                    <div className="pf-settings-editor__mobile-nav" id="storefront-settings-mobile-nav">
                                        <Card padding="0">
                                            <Box padding="300">
                                                {renderSettingsNavButtons(true)}
                                            </Box>
                                        </Card>
                                    </div>
                                ) : null}
                                {appearanceSelected && (
                                    <div className="pf-appearance-workspace">
                                        <section className="pf-appearance-workspace__preview" aria-label="Appearance preview">
                                            {appProxyResultsEnabled ? (
                                                <AppearancePreview uiSettings={uiSettings} />
                                            ) : (
                                                <Banner tone="info">
                                                    <Text as="p">
                                                        Switch Search result mode to App proxy results page to preview and edit proxy UI appearance.
                                                    </Text>
                                                </Banner>
                                            )}
                                        </section>
                                        <aside className="pf-appearance-workspace__controls" aria-label="Appearance controls">
                                            <Card>
                                                <Box padding="400">
                                                    {appProxyResultsEnabled ? (
                                                        <AppearanceControls
                                                            uiSettings={uiSettings}
                                                            updateUiSettingsDraft={updateUiSettingsDraft}
                                                            updateUiNumberSetting={updateUiNumberSetting}
                                                        />
                                                    ) : (
                                                        <BlockStack gap="300">
                                                            <Text as="h2" variant="headingMd">Appearance</Text>
                                                            <Text as="p" tone="subdued">
                                                                App proxy appearance controls are available when the proxy results mode is selected.
                                                            </Text>
                                                        </BlockStack>
                                                    )}
                                                </Box>
                                            </Card>
                                        </aside>
                                    </div>
                                )}
                                {selectedTab === 0 && (
                        <Layout>
                            <Layout.Section>
                                {loadingPrimarySettings ? (
                                    <Card>
                                        <Box padding="400">
                                            <SkeletonBodyText lines={8} />
                                        </Box>
                                    </Card>
                                ) : (
                                    <BlockStack gap="400">
                                        {resultsError ? (
                                            <Banner tone="critical">
                                                <Text as="p">{resultsError}</Text>
                                            </Banner>
                                        ) : null}
                                        {resultsSuccess ? (
                                            <Banner tone="success">
                                                <Text as="p">{resultsSuccess}</Text>
                                            </Banner>
                                        ) : null}

                                        <SettingsCard
                                            title="Search result mode"
                                            description="Choose how matching products are displayed after a customer searches."
                                        >
                                            <div
                                                className="pf-settings-choice-grid pf-settings-choice-grid--three"
                                                role="radiogroup"
                                                aria-label="Search result mode"
                                            >
                                                {modeOptions.map((option) => (
                                                    <SearchResultModeCard
                                                        key={option.value}
                                                        id={`search-result-mode-${option.value}`}
                                                        label={option.label}
                                                        description={option.description}
                                                        checked={(resultsDraft?.renderingMode ?? "CollectionFiltering") === option.value}
                                                        onChange={() => updateRenderingMode(option.value)}
                                                    />
                                                ))}
                                            </div>
                                        </SettingsCard>

                                        {resultsDraft?.renderingMode === "CollectionFiltering" ? (
                                            <SettingsCard
                                                title="Shopify filtering method"
                                                description="Choose how PartFits applies the selected vehicle tag to Shopify collection pages."
                                            >
                                                <BlockStack gap="300">
                                                    <SettingsRadioGroup
                                                        hideLabel
                                                        label="Shopify filtering method"
                                                        name="shopify-filtering-method"
                                                        options={shopifyFilteringMethodOptions}
                                                        value={shopifyFilteringMethod}
                                                        onChange={(value) => updateThemeDraft(
                                                            "collectionTagFilterEnabled",
                                                            value === "StorefrontFiltering"
                                                        )}
                                                    />
                                                    {shopifyFilteringMethod === "StorefrontFiltering" ? (
                                                        <Banner tone="info" title="Search & Discovery setup required">
                                                            <BlockStack gap="200">
                                                                <Text as="p" variant="bodySm">
                                                                    Add the Tags filter and confirm that the active theme supports storefront filtering. Shopify limits native filtering on collections over 5,000 products, and product-tag filters are available only in the store&apos;s default language.
                                                                </Text>
                                                                <Link
                                                                    url="https://help.shopify.com/en/manual/online-store/storefront-search/search-and-discovery-filters"
                                                                    target="_blank"
                                                                >
                                                                    Review Shopify&apos;s filtering setup and limits
                                                                </Link>
                                                            </BlockStack>
                                                        </Banner>
                                                    ) : null}
                                                </BlockStack>
                                            </SettingsCard>
                                        ) : null}

                                        <SettingsCard
                                            title="Search bar behavior"
                                            description="Control when the search runs and how searches behave from collection pages."
                                        >
                                            <BlockStack gap="400">
                                                <SettingsRadioGroup
                                                    label="When to search"
                                                    name="search-trigger"
                                                    options={searchTriggerOptions}
                                                    value={themeDraft?.automaticSearch ? "Automatic" : "Manual"}
                                                    onChange={(value) => updateThemeDraft("automaticSearch", value === "Automatic")}
                                                />
                                                {!appProxyResultsEnabled ? (
                                                    <SettingsRadioGroup
                                                        label="Search destination"
                                                        name="search-destination"
                                                        options={searchDestinationOptions}
                                                        value={searchDestinationMode}
                                                        onChange={(value) => updateSearchDestinationMode(value as SearchDestinationMode)}
                                                    />
                                                ) : null}
                                                <Divider />
                                                <SettingCheckbox
                                                    label="Enable searchable vehicle dropdowns"
                                                    checked={!!themeDraft?.searchableDropdownsEnabled}
                                                    onChange={(value) => updateThemeDraft("searchableDropdownsEnabled", value)}
                                                    description="Lets shoppers type inside vehicle selector dropdowns to filter options before selecting."
                                                />
                                            </BlockStack>
                                        </SettingsCard>

                                        <SettingsCard
                                            title={resultsSettingsTitle}
                                            description={resultsSettingsDescription}
                                        >
                                            <BlockStack gap="400">
                                                {!appProxyResultsEnabled ? (
                                                    <>
                                                        <SettingsRadioGroup
                                                            label={renderedResultsEnabled ? "Results page location" : "Fallback results page"}
                                                            name="results-page-location"
                                                            options={resultsPageLocationOptions}
                                                            value={resultsPageLocationMode}
                                                            onChange={(value) => updateResultsPageLocationMode(value as ResultsPageLocationMode)}
                                                        />

                                                        {resultsPageLocationMode === "Custom" ? (
                                                            <TextField
                                                                label={renderedResultsEnabled ? "App results page path" : "Custom results path"}
                                                                autoComplete="off"
                                                                value={themeDraft?.redirectPath || ""}
                                                                placeholder={renderedResultsEnabled ? "/pages/partfinder-results" : "/collections/all"}
                                                                onChange={(value) => updateThemeDraft("redirectPath", value)}
                                                                helpText={renderedResultsEnabled
                                                                    ? "Use the page path that hosts the PartFits Results block. Do not include {vehicle}; the app reads the selected vehicle from PartFits state."
                                                                    : "Enter the base collection path, such as /collections/all. PartFits applies the selected Shopify filtering method. Existing {vehicle} placeholders remain supported for migrated stores."}
                                                            />
                                                        ) : null}
                                                    </>
                                                ) : null}

                                                {appRenderedResultsEnabled ? (
                                                    <BlockStack gap="400">
                                                        {!appProxyResultsEnabled ? <Divider /> : null}
                                                        <SettingCheckbox
                                                            label="Show prices"
                                                            checked={resultsDraft?.priceDisplayMode !== "HidePrices"}
                                                            onChange={(checked) => updateResultsDraft({ priceDisplayMode: checked ? "MarketAwarePrices" : "HidePrices" })}
                                                            description="Use Shopify Markets pricing when available, with store-default variant prices as the fallback."
                                                        />
                                                        <SettingsRadioGroup
                                                            label="Variant results"
                                                            name="variant-results"
                                                            options={variantResultDisplayOptions}
                                                            value={resultsDraft?.variantResultDisplayMode ?? "TopLevelProducts"}
                                                            onChange={(value) => updateResultsDraft({ variantResultDisplayMode: value as StorefrontVariantResultDisplayMode })}
                                                        />
                                                        <Divider />
                                                        <Select
                                                            label="Organize results by"
                                                            options={groupingOptions}
                                                            value={resultsDraft?.groupResultsBy ?? "None"}
                                                            onChange={(value) => updateResultsDraft({ groupResultsBy: value as StorefrontResultsGroupingMode })}
                                                        />

                                                        {resultsDraft?.groupResultsBy === "ProductType" ? (
                                                            <BlockStack gap="300">
                                                                <Select
                                                                    label="Product type groups"
                                                                    options={resultFilterModeOptions}
                                                                    value={resultsDraft.productTypeFilterMode ?? "All"}
                                                                    onChange={(value) => updateResultsDraft({ productTypeFilterMode: value as StorefrontResultFilterMode })}
                                                                    helpText="Use this when some synced product types should not appear as result groups."
                                                                />
                                                                {resultsDraft.productTypeFilterMode !== "All" ? (
                                                                    <BlockStack gap="300">
                                                                        <TextField
                                                                            label="Filter product types"
                                                                            autoComplete="off"
                                                                            value={productTypeSearch}
                                                                            onChange={setProductTypeSearch}
                                                                            placeholder="Search product types"
                                                                        />
                                                                        <div style={{ border: "1px solid #ebebeb", borderRadius: 8, maxHeight: 260, overflowY: "auto", padding: 12 }}>
                                                                            <BlockStack gap="200">
                                                                                {filteredProductTypes.length === 0 ? (
                                                                                    <Text as="p" tone="subdued">No product types found.</Text>
                                                                                ) : filteredProductTypes.map((productType) => (
                                                                                    <Checkbox
                                                                                        key={productType}
                                                                                        label={productType}
                                                                                        checked={selectedProductTypeFilterValues.has(productType)}
                                                                                        onChange={(checked) => toggleProductTypeFilterValue(productType, checked)}
                                                                                    />
                                                                                ))}
                                                                            </BlockStack>
                                                                        </div>
                                                                        <Text as="p" variant="bodySm" tone="subdued">
                                                                            {selectedProductTypeFilterValues.size} product type{selectedProductTypeFilterValues.size === 1 ? "" : "s"} selected.
                                                                        </Text>
                                                                    </BlockStack>
                                                                ) : null}
                                                            </BlockStack>
                                                        ) : null}

                                                        {resultsDraft?.groupResultsBy === "Collection" ? (
                                                            <BlockStack gap="300">
                                                                <Select
                                                                    label="Collection groups"
                                                                    options={resultFilterModeOptions}
                                                                    value={resultsDraft.collectionFilterMode ?? "All"}
                                                                    onChange={(value) => updateResultsDraft({ collectionFilterMode: value as StorefrontResultFilterMode })}
                                                                    helpText="Use this when some collections should not appear as result groups."
                                                                />
                                                                {resultsDraft.collectionFilterMode !== "All" ? (
                                                                    <BlockStack gap="300">
                                                                        <TextField
                                                                            label="Filter collections"
                                                                            autoComplete="off"
                                                                            value={collectionGroupSearch}
                                                                            onChange={setCollectionGroupSearch}
                                                                            placeholder="Search collections"
                                                                        />
                                                                        <div style={{ border: "1px solid #ebebeb", borderRadius: 8, maxHeight: 260, overflowY: "auto", padding: 12 }}>
                                                                            <BlockStack gap="200">
                                                                                {filteredGroupCollections.length === 0 ? (
                                                                                    <Text as="p" tone="subdued">No collections found.</Text>
                                                                                ) : filteredGroupCollections.map((collection) => (
                                                                                    <Checkbox
                                                                                        key={collection.id}
                                                                                        label={collectionLabel(collection)}
                                                                                        checked={selectedCollectionFilterIds.has(collection.id)}
                                                                                        onChange={(checked) => toggleCollectionFilterValue(collection.id, checked)}
                                                                                    />
                                                                                ))}
                                                                            </BlockStack>
                                                                        </div>
                                                                        <Text as="p" variant="bodySm" tone="subdued">
                                                                            {selectedCollectionFilterIds.size} collection{selectedCollectionFilterIds.size === 1 ? "" : "s"} selected.
                                                                        </Text>
                                                                    </BlockStack>
                                                                ) : null}
                                                            </BlockStack>
                                                        ) : null}

                                                        {resultsDraft?.groupResultsBy === "CuratedGroups" ? (
                                                            <BlockStack gap="400">
                                                                <Divider />
                                                                <InlineStack align="space-between" blockAlign="center" gap="300">
                                                                    <BlockStack gap="100">
                                                                        <Text as="h3" variant="headingSm">Custom result groups</Text>
                                                                        <Text as="p" tone="subdued">
                                                                            Build merchant-defined result sections by mapping Shopify collections into named groups.
                                                                        </Text>
                                                                    </BlockStack>
                                                                    <Button icon={PlusIcon} onClick={openAddGroupModal}>Add group</Button>
                                                                </InlineStack>

                                                                {sortedGroups.length === 0 ? (
                                                                    <Box padding="300">
                                                                        <Text as="p" tone="subdued">
                                                                            No custom result groups have been added yet.
                                                                        </Text>
                                                                    </Box>
                                                                ) : (
                                                                    <IndexTable
                                                                        resourceName={{ singular: "group", plural: "groups" }}
                                                                        itemCount={sortedGroups.length}
                                                                        selectable={false}
                                                                        headings={[
                                                                            { title: "Group" },
                                                                            { title: "Include" },
                                                                            { title: "Collections" },
                                                                            { title: "Display order" },
                                                                            { title: "Actions" },
                                                                        ]}
                                                                    >
                                                                        {sortedGroups.map(({ group, originalIndex }, index) => {
                                                                            const collectionSummary = group.collectionIds
                                                                                .map((id) => collectionMap.get(id)?.title)
                                                                                .filter(Boolean)
                                                                                .join(", ");

                                                                            return (
                                                                                <IndexTable.Row id={`${group.id ?? "new"}-${index}`} key={`${group.id ?? "new"}-${index}`} position={index}>
                                                                                    <IndexTable.Cell>
                                                                                        <Text as="span" variant="bodyMd" fontWeight="bold">{group.name}</Text>
                                                                                    </IndexTable.Cell>
                                                                                    <IndexTable.Cell>
                                                                                        {group.include ? <Badge tone="success">Yes</Badge> : <Badge>Hidden</Badge>}
                                                                                    </IndexTable.Cell>
                                                                                    <IndexTable.Cell>
                                                                                        <Text as="span" tone="subdued">
                                                                                            {collectionSummary || "No collections selected"}
                                                                                        </Text>
                                                                                    </IndexTable.Cell>
                                                                                    <IndexTable.Cell>{group.displayOrder}</IndexTable.Cell>
                                                                                    <IndexTable.Cell>
                                                                                        <InlineStack gap="100" wrap={false}>
                                                                                            <Button icon={EditIcon} variant="tertiary" onClick={() => openEditGroupModal(originalIndex)}>
                                                                                                Edit
                                                                                            </Button>
                                                                                            <Button icon={DeleteIcon} variant="tertiary" tone="critical" onClick={() => deleteGroup(originalIndex)}>
                                                                                                Delete
                                                                                            </Button>
                                                                                        </InlineStack>
                                                                                    </IndexTable.Cell>
                                                                                </IndexTable.Row>
                                                                            );
                                                                        })}
                                                                    </IndexTable>
                                                                )}
                                                            </BlockStack>
                                                        ) : null}
                                                    </BlockStack>
                                                ) : null}
                                            </BlockStack>
                                        </SettingsCard>


                                    </BlockStack>
                                )}
                            </Layout.Section>
                        </Layout>
                    )}

                    {fitmentFieldsMounted && (
                        <div hidden={selectedTab !== SELECTOR_FIELDS_SECTION_INDEX && selectedTab !== REFERENCE_DATA_SECTION_INDEX}>
                            <Layout>
                                <FitmentFieldsSettings
                                    onStatusChange={setFitmentFieldsEditor}
                                    onOpenVendorIntegrations={openVendorIntegrationsSettings}
                                    section={selectedTab === REFERENCE_DATA_SECTION_INDEX ? "referenceData" : "fields"}
                                    showSaveBar={false}
                                />
                            </Layout>
                        </div>
                    )}

                    {attributesMounted && (
                        <div hidden={selectedTab !== ATTRIBUTES_SECTION_INDEX}>
                            <Layout>
                                <Layout.Section>
                                    <MetafieldAttributesSettings
                                        onStatusChange={setAttributeEditor}
                                        onOpenShopifySync={openShopifySyncSettings}
                                    />
                                </Layout.Section>
                            </Layout>
                        </div>
                    )}

                    {selectedTab === DATA_REFRESH_SECTION_INDEX && (
                        <Layout>
                            <Layout.Section>
                                <DataSyncSettings onOpenProductAttributes={openProductAttributesSettings} />
                            </Layout.Section>
                        </Layout>
                    )}

                    {integrationsMounted && (
                        <div hidden={selectedTab !== INTEGRATIONS_SECTION_INDEX}>
                            <Layout>
                                <Layout.Section>
                                    <BlockStack gap="400">
                                        <Turn14IntegrationSettings
                                            onStatusChange={setIntegrationEditor}
                                            onOpenFitmentDataSources={openFitmentDataSourcesSettings}
                                        />
                                        <AtdIntegrationSettings />
                                    </BlockStack>
                                </Layout.Section>
                            </Layout>
                        </div>
                    )}

                    {translationsMounted && (
                        <div hidden={selectedTab !== STOREFRONT_TEXT_SECTION_INDEX}>
                            <Layout>
                                <Layout.Section>
                                    <StorefrontTranslationsSettings
                                        onStatusChange={setTranslationEditor}
                                        onHeaderActionsChange={setStorefrontTextHeaderActions}
                                    />
                                </Layout.Section>
                            </Layout>
                        </div>
                    )}

                    {selectedTab === ADVANCED_SECTION_INDEX && (
                        <Layout>
                            <Layout.Section>
                                {loadingThemeSettings || !themeDraft ? (
                                    <LoadingSettingsCard label="Advanced storefront settings" />
                                ) : (
                                    <BlockStack gap="400">
                                        <SettingsCard
                                            title="Advanced search behavior"
                                            description="These options are useful for stores with custom navigation or external fitment data."
                                        >
                                            <SettingCheckbox
                                                label="Only show mapped user vehicles"
                                                checked={themeDraft?.onlyShowMappedUserVehicles ?? true}
                                                onChange={(value) => updateThemeDraft("onlyShowMappedUserVehicles", value)}
                                                description="Recommended. Selector options only include user-managed vehicles that are mapped to products."
                                                help="Disable only if fitments are managed outside the app, such as directly with product tags."
                                            />
                                            <Divider />
                                            <SettingCheckbox
                                                label="Keep collection links filtered"
                                                checked={!!persistentFilteringChecked}
                                                onChange={setPersistentFilteringChecked}
                                                description="Collection links automatically include the selected vehicle filter."
                                                help="This keeps shoppers in the context of their selected vehicle as they navigate collections."
                                            />
                                        </SettingsCard>

                                        <SettingsCard
                                            title="Advanced URL behavior"
                                            description="Use these only when a theme or Shopify filter setup needs a specific URL format."
                                        >
                                            {themeDraft.collectionTagFilterEnabled ? (
                                                <BlockStack gap="200">
                                                    <InlineStack align="space-between" blockAlign="center" gap="300">
                                                        <BlockStack gap="100">
                                                            <Text as="h3" variant="headingSm">
                                                                Storefront filter URL override
                                                            </Text>
                                                            <Text as="p" variant="bodySm" tone="subdued">
                                                                The default is <code>{"?filter.p.tag={vehicle}"}</code>. Change it only when a custom Shopify filter implementation requires another parameter.
                                                            </Text>
                                                        </BlockStack>
                                                        <Button
                                                            onClick={() => setFilterPatternOverrideOpen((open) => !open)}
                                                            ariaExpanded={filterPatternOverrideOpen}
                                                            ariaControls="storefront-filter-pattern-override"
                                                        >
                                                            {filterPatternOverrideOpen ? "Hide override" : "Show override"}
                                                        </Button>
                                                    </InlineStack>
                                                    <Collapsible
                                                        id="storefront-filter-pattern-override"
                                                        open={filterPatternOverrideOpen}
                                                    >
                                                        <Box paddingBlockStart="200" paddingBlockEnd="300">
                                                            <TextField
                                                                label="Querystring filter pattern"
                                                                autoComplete="off"
                                                                value={themeDraft.collectionTagFilterPattern || ""}
                                                                onChange={(value) => updateThemeDraft("collectionTagFilterPattern", value)}
                                                                helpText="Use {vehicle} as the selected vehicle placeholder. PartFits safely URL-encodes the final tag value."
                                                            />
                                                        </Box>
                                                    </Collapsible>
                                                </BlockStack>
                                            ) : (
                                                <Box paddingBlockEnd="300">
                                                    <Text as="p" variant="bodySm" tone="subdued">
                                                        Querystring overrides are available when Shopify storefront filtering is selected on the Overview tab.
                                                    </Text>
                                                </Box>
                                            )}
                                            <Divider />
                                            <SettingCheckbox
                                                label="Stay on current page after Clear button click"
                                                checked={!!themeDraft.clearButtonRedirectEnabled}
                                                onChange={(value) => updateThemeDraft("clearButtonRedirectEnabled", value)}
                                                description="Clear only resets the selector instead of redirecting to remove vehicle filtering."
                                            />
                                        </SettingsCard>

                                        <SettingsCard
                                            title="Advanced compatibility"
                                            description="Most stores can leave these unchanged. Use these for custom templates, special mapping strategies, or customer profile tagging."
                                        >
                                        <SettingCheckbox
                                            label="Allow make selection through collection metafield"
                                            checked={!!themeDraft.isCollectionMetaField}
                                            onChange={(value) => updateThemeDraft("isCollectionMetaField", value)}
                                            description="Uses collection metafield data to preselect the make on collection pages."
                                        />
                                        <Divider />
                                        <SettingCheckbox
                                            label="Filtered collection fallback"
                                            checked={!!themeDraft.collectionRedirectEnabled}
                                            onChange={(value) => updateThemeDraft("collectionRedirectEnabled", value)}
                                            description="When no vehicle-to-collection mapping exists, falls back to Shopify collection/tag filtering."
                                            help="Mappings are managed from Products > Collections. Storefront search currently redirects mapped vehicles to their collection before applying this fallback."
                                        />
                                        <Divider />
                                        <SettingCheckbox
                                            label="Save tags to customer profile"
                                            checked={!!themeDraft.saveTagToCustomerProfile}
                                            onChange={(value) => updateThemeDraft("saveTagToCustomerProfile", value)}
                                            description="Saves selected garage vehicle tags to the Shopify customer profile."
                                            help="Only applies when the shopper is signed in and the storefront can identify the customer."
                                        />
                                        <Box paddingBlockStart="400">
                                            <BlockStack gap="200">
                                                <InlineStack align="space-between" blockAlign="center" gap="300">
                                                    <BlockStack gap="100">
                                                        <Text as="h3" variant="headingSm">
                                                            Legacy settings
                                                        </Text>
                                                        <Text as="p" variant="bodySm" tone="subdued">
                                                            Review these when migrating legacy stores. Modern selector fields now control most of this behavior.
                                                        </Text>
                                                    </BlockStack>
                                                    <Button
                                                        onClick={() => setLegacySettingsOpen((open) => !open)}
                                                        ariaExpanded={legacySettingsOpen}
                                                        ariaControls="legacy-settings"
                                                    >
                                                        {legacySettingsOpen ? "Hide legacy settings" : "Show legacy settings"}
                                                    </Button>
                                                </InlineStack>
                                                <Collapsible id="legacy-settings" open={legacySettingsOpen}>
                                                    <BlockStack gap="0">
                                                        <SettingCheckbox
                                                            label="Enable submodel filter"
                                                            checked={submodelEnabled}
                                                            onChange={setSubmodelEnabled}
                                                            description="Legacy storefront toggle for showing a submodel dropdown."
                                                            help="Modern selector fields now control whether Submodel / Trim appears and whether it is required."
                                                        />
                                                        <Divider />
                                                        <SettingCheckbox
                                                            label="Arrange search inputs in Year, Make, Model order"
                                                            checked={!!themeDraft.ymmEnabled}
                                                            onChange={(value) => updateThemeDraft("ymmEnabled", value)}
                                                            description="Legacy selector ordering flag. Modern selector fields now control display order."
                                                        />
                                                        <Divider />
                                                        <SettingCheckbox
                                                            label="Sort years in ascending order"
                                                            checked={!!themeDraft.sortYearsASC}
                                                            onChange={(value) => updateThemeDraft("sortYearsASC", value)}
                                                            description="Legacy year sorting flag. Modern selector fields now control per-field sort order."
                                                        />
                                                        <Divider />
                                                        <SettingCheckbox
                                                            label="Partial search"
                                                            checked={!!themeDraft.isPartialSearch}
                                                            onChange={(value) => updateThemeDraft("isPartialSearch", value)}
                                                            description="Legacy partial-search flag. Modern selector fields now use Require before search per field."
                                                        />
                                                        <Divider />
                                                        <SettingCheckbox
                                                            label="Supplement mappings with Make-Model tag"
                                                            checked={!!themeDraft.useTwoTags}
                                                            onChange={(value) => updateThemeDraft("useTwoTags", value)}
                                                            description="Adds a Make-Model tag for each mapped vehicle."
                                                        />
                                                        <Divider />
                                                        <SettingCheckbox
                                                            label="Supplement mappings with Make and Make-Model tags"
                                                            checked={!!themeDraft.useThreeTags}
                                                            onChange={(value) => updateThemeDraft("useThreeTags", value)}
                                                            description="Adds Make and Make-Model tags for each mapped vehicle."
                                                        />
                                                        <Divider />
                                                        <SettingCheckbox
                                                            label="Include universal collections"
                                                            checked={!!themeDraft.includeUniversalCollections}
                                                            onChange={(value) => updateThemeDraft("includeUniversalCollections", value)}
                                                            description="Includes universal collections in categorized result views."
                                                            help="This requires custom theme support before shoppers will see the intended result."
                                                        />
                                                        <Divider />
                                                        <SettingCheckbox
                                                            label="Sync collection metadata"
                                                            checked={!!themeDraft.metaDataSyncEnabled}
                                                            onChange={(value) => updateThemeDraft("metaDataSyncEnabled", value)}
                                                            description="Keeps collection metadata aligned for stores that still depend on it."
                                                        />
                                                    </BlockStack>
                                                </Collapsible>
                                            </BlockStack>
                                        </Box>
                                        </SettingsCard>
                                    </BlockStack>
                                )}
                            </Layout.Section>
                        </Layout>
                    )}

                    {selectedTab === PLAN_SECTION_INDEX && <PlanPage embedded />}
                            </main>
                        </div>
                    </div>
                </div>
            </AppBridgeModal>

            <SaveBar id="storefront-settings-save-bar" open={settingsEditorOpen && hasUnsavedChanges}>
                <button onClick={handleDiscard} disabled={editorActionInProgress}>
                    Discard
                </button>
                <button variant="primary" onClick={handleSave} disabled={editorActionInProgress}>
                    Save
                </button>
            </SaveBar>

            <AppBridgeModal
                id="storefront-unsaved-close-confirmation"
                open={confirmCloseOpen}
                onHide={handleConfirmCloseHide}
                variant="small"
            >
                <div style={{ padding: 16 }}>
                    <BlockStack gap="300">
                        <Text as="p" variant="bodyMd">
                            You have unsaved storefront settings. Leave without saving?
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                            Staying keeps your edits open so you can save or discard them.
                        </Text>
                    </BlockStack>
                </div>
                <TitleBar title="Unsaved changes">
                    <button tone="critical" onClick={leaveSettingsEditor}>
                        Leave without saving
                    </button>
                    <button variant="primary" onClick={stayInSettingsEditor}>
                        Stay
                    </button>
                </TitleBar>
            </AppBridgeModal>

            <Modal
                open={groupModalOpen}
                onClose={closeGroupModal}
                title={editingGroupIndex === undefined ? "Add curated group" : "Edit curated group"}
                primaryAction={{
                    content: editingGroupIndex === undefined ? "Add group" : "Save group",
                    onAction: saveGroupDraft,
                    disabled: !canSaveGroup,
                }}
                secondaryActions={[
                    {
                        content: "Cancel",
                        onAction: closeGroupModal,
                    },
                ]}
            >
                <Modal.Section>
                    {groupDraft ? (
                        <BlockStack gap="400">
                            <BlockStack gap="400">
                                <TextField
                                    label="Group name"
                                    autoComplete="off"
                                    value={groupDraft.name}
                                    placeholder="Example: Lighting"
                                    onChange={(value) => updateGroupDraft({ name: value })}
                                />
                                <TextField
                                    label="Display order"
                                    autoComplete="off"
                                    type="number"
                                    min={1}
                                    value={String(groupDraft.displayOrder || "")}
                                    onChange={(value) => updateGroupDraft({ displayOrder: Number(value) || 0 })}
                                />
                            </BlockStack>

                            <Checkbox
                                label="Include this group in rendered results"
                                checked={groupDraft.include}
                                onChange={(checked) => updateGroupDraft({ include: checked })}
                            />

                            <Divider />

                            <BlockStack gap="300">
                                <BlockStack gap="100">
                                    <Text as="h3" variant="headingSm">Mapped collections</Text>
                                    <Text as="p" tone="subdued">
                                        Products from these collections will be eligible for this group when collection membership is available to the renderer.
                                    </Text>
                                </BlockStack>
                                <TextField
                                    label="Filter collections"
                                    labelHidden
                                    autoComplete="off"
                                    value={collectionFilter}
                                    placeholder="Filter collections"
                                    onChange={setCollectionFilter}
                                />

                                <div style={{ border: "1px solid #ebebeb", borderRadius: 8, maxHeight: 320, overflowY: "auto", padding: 12 }}>
                                    <BlockStack gap="200">
                                        {filteredCollections.length === 0 ? (
                                            <Text as="p" tone="subdued">No collections found.</Text>
                                        ) : filteredCollections.map((collection) => (
                                            <Checkbox
                                                key={collection.id}
                                                label={collectionLabel(collection)}
                                                checked={selectedCollectionIds.has(collection.id)}
                                                onChange={(checked) => toggleCollection(collection.id, checked)}
                                            />
                                        ))}
                                    </BlockStack>
                                </div>
                            </BlockStack>
                        </BlockStack>
                    ) : null}
                </Modal.Section>
            </Modal>
        </>
    );
};

export default observer(StorefrontPage);
